import { useState, useRef, useEffect, useCallback, KeyboardEvent } from "react";
import {
    Calendar, Check, Loader2, Mail, MapPin, Store, Zap, X,
    Search, Tag, Building2, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Plus, Type,
    Star, Users, Send, SlidersHorizontal, ArrowLeft, Clock, Trash2, RefreshCw,
    Power, PencilLine, Eye, MessageSquare, AlertTriangle, Hash, Lock,
    Sparkles, Tags, ExternalLink, Newspaper, ArrowUp, ArrowDown,
    ArrowLeftRight, CalendarPlus, Info, RotateCcw, Ban, SkipForward,
} from "lucide-react";
import BONavigation from "components/admin/BONavigation";
import {
    listProfiles, getProfile, createProfile, updateProfile, deleteProfile,
    prepareDraft, getDraft, updateDraft, addDraftCard, sendDraftNow, sendDraftTest, cancelDraft,
    regenerateDraft, addAutoCard, getDraftTypologies, getDraftNews,
    getProfileCalendar, createOccurrence, updateOccurrence, prepareOccurrence,
    listFeedback, getProfileFeedback, getProfileHistory, searchByTypology,
    searchEstablishments, getEstablishmentsByIds,
    findEstablishmentsBySiret, generateWeeklyMail, feedbackPageUrl,
    WeeklyMailProfile, WeeklyMailProfileFilters, WeeklyMailDraft, WeeklyMailDraftCard,
    WeeklyMailRecipient, WeeklyMailSchedule, WeeklyMailTimeSlot, WeeklyEstablishment, WeeklyMailFeedbackItem,
    WeeklyMailProfileFeedback, WeeklyMailHistory, WeeklyMailSendEntry,
    WeeklySentEstablishment, TypologySearchResult, WeeklyDraftTypology,
    WeeklyPressItem, WeeklyZoneNews, WeeklyMailCalendar, WeeklyCalendarItem, WeeklyCalendarStatus,
    PREVIEW_LEAD_DAYS, MIN_ESTABLISHMENTS,
} from "interfaces/weeklyMail";
import { ClientTypology, listTypologies } from "interfaces/clientProfiles";
import { getEstablishmentTags, getUniqueNafCodes, NafCodeOption } from "interfaces/establishments";

// ─── constants ────────────────────────────────────────────────────────────────

// No "Fermeture": closures are excluded from briefs backend-side (EXCLUDED_SIGNAL_TYPES),
// so offering the type here could only ever produce an empty brief.
const SIGNAL_TYPES = [
    { value: "restaurant_opening", label: "Ouverture" },
    { value: "franchise_signing", label: "Signature franchise" },
    { value: "network_expansion", label: "Expansion réseau" },
    { value: "multi_site", label: "Multi-site" },
    { value: "restaurant_owner_change", label: "Changement propriétaire" },
    { value: "hygiene_control", label: "Contrôle hygiène" },
    { value: "bodacc_announcement", label: "Annonce BODACC" },
];

const FRENCH_REGIONS = [
    "Auvergne-Rhône-Alpes", "Bourgogne-Franche-Comté", "Bretagne",
    "Centre-Val de Loire", "Corse", "Grand Est", "Guadeloupe",
    "Guyane", "Hauts-de-France", "Île-de-France", "La Réunion",
    "Martinique", "Mayotte", "Normandie", "Nouvelle-Aquitaine",
    "Occitanie", "Pays de la Loire", "Provence-Alpes-Côte d'Azur",
];

const DAYS = ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];

const SLOT_STYLES: Record<string, string> = {
    signal: "bg-gray-100 text-gray-600",
    hygiene: "bg-amber-50 text-amber-700",
    multi_owner: "bg-blue-50 text-blue-700",
    rich_contacts: "bg-teal-50 text-teal-700",
    manual: "bg-violet-50 text-violet-700",
    backfill: "bg-pink-50 text-pink-700",
};

const SLOT_LABELS: Record<string, string> = {
    signal: "Signal de la période",
    hygiene: "Activité confirmée",
    multi_owner: "Multi-établissements",
    rich_contacts: "Coordonnées complètes",
    manual: "Sélection manuelle",
    backfill: "Complément de zone",
};

const EMPTY_FILTERS: WeeklyMailProfileFilters = {
    cities: [], regions: [], departments: [], nafCodes: [],
    signalTypes: ["restaurant_opening"], categories: [], subCategories: [],
    lookbackDays: 7, maxEstablishments: 7,
    slots: { hygiene: true, multiOwner: true, richContacts: true },
    manualIds: [],
};

// ─── helpers ──────────────────────────────────────────────────────────────────

// A sandboxed iframe swallows link clicks unless popups are allowed, and any
// navigation would replace the preview itself — so previewed mails open their
// links in a new tab.
const PREVIEW_SANDBOX = "allow-same-origin allow-popups allow-popups-to-escape-sandbox";

function previewDoc(html: string): string {
    return html.replace(/<head>/i, `<head><base target="_blank">`);
}

function fmtDate(iso: string) {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
}
function fmtDateTime(iso: string) {
    return new Date(iso).toLocaleString("fr-FR", {
        weekday: "long", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
    });
}
const DAYS_SHORT = ["dim.", "lun.", "mar.", "mer.", "jeu.", "ven.", "sam."];
const hhmm = (h: number, m: number) => `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;

/**
 * The weekly slots of a schedule, Monday first. A profile saved before several slots existed
 * only carries the single day/hour, read as a one-slot week — as the server does.
 */
function scheduleSlots(s: WeeklyMailSchedule): WeeklyMailTimeSlot[] {
    const list = s.times?.length ? s.times : [{ dayOfWeek: s.dayOfWeek, hour: s.hour, minute: s.minute }];
    return [...list].sort((a, b) =>
        ((a.dayOfWeek + 6) % 7) - ((b.dayOfWeek + 6) % 7) || a.hour - b.hour || a.minute - b.minute
    );
}

function scheduleLabel(s: WeeklyMailSchedule) {
    const slots = scheduleSlots(s);
    const when = slots.length === 1
        ? `Chaque ${DAYS[slots[0].dayOfWeek].toLowerCase()} à ${hhmm(slots[0].hour, slots[0].minute)}`
        : `${slots.length} envois par semaine (${slots.map((t) => `${DAYS_SHORT[t.dayOfWeek]} ${hhmm(t.hour, t.minute)}`).join(", ")})`;
    const count = s.occurrences === null
        ? "envois illimités"
        : `${Math.max(0, s.occurrences - s.sentCount)} envoi${s.occurrences - s.sentCount > 1 ? "s" : ""} restant${s.occurrences - s.sentCount > 1 ? "s" : ""}`;
    return `${when} · ${count}`;
}

/** The bare owner name of a card: stored since links exist, read off the owner line before that. */
function cardOwnerName(card: { owner: string | null; ownerName?: string | null }): string | null {
    const name = (card.ownerName ?? card.owner?.split(" · ")[0] ?? "").trim();
    return name || null;
}

/** The app's own pages for an establishment and for its owner — the same ones the mail links to. */
const establishmentPage = (id: string) => `/establishments/${id}`;
const ownerPage = (name: string) => `/signals/founder?founderName=${encodeURIComponent(name)}`;
function daysUntil(iso: string) {
    return Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
}

// ─── Resend block ─────────────────────────────────────────────────────────────

/**
 * Marks a row that cannot go out, and says which rule holds it. Shown rather than hidden: a
 * row that silently disappears from a search reads as a missing record, while one that names
 * the rule and the date answers the question the admin was about to ask.
 */
function BlockedBadge({ est }: { est: WeeklyEstablishment }) {
    const reason = est.resendBlockReason;
    if (!reason) return null;

    const until = est.resendBlockedUntil ? fmtDate(est.resendBlockedUntil) : null;
    const label =
        reason === "recipient" ? `Déjà envoyé à ce client · libre le ${until}`
            : reason === "cross_client" ? `Envoyé à un autre client · libre le ${until}`
            : "Déjà dans un envoi en préparation";
    const title =
        reason === "recipient" ? `Envoyé le ${fmtDate(est.lastSentAt!)}. Un établissement n'est pas renvoyé au même destinataire avant 6 mois.`
            : reason === "cross_client" ? `Envoyé le ${fmtDate(est.lastSentAt!)} à un autre client. Un établissement n'est pas repris avant 3 semaines, quel que soit le destinataire.`
            : "Un autre briefing en préparation le contient déjà : il partira avant celui-ci.";

    return (
        <span
            title={title}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded whitespace-nowrap ${
                reason === "pending_draft" ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-700"
            }`}
        >
            <Lock className="w-2.5 h-2.5" />
            {label}
        </span>
    );
}

// ─── ChipInput ────────────────────────────────────────────────────────────────

function ChipInput({ values, onChange, placeholder }: {
    values: string[]; onChange: (v: string[]) => void; placeholder?: string;
}) {
    const [input, setInput] = useState("");
    const ref = useRef<HTMLInputElement>(null);

    const add = () => {
        const t = input.trim();
        if (t && !values.includes(t)) onChange([...values, t]);
        setInput("");
    };
    const handleKey = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); }
        else if (e.key === "Backspace" && !input && values.length) onChange(values.slice(0, -1));
    };

    return (
        <div
            className="flex flex-wrap gap-1.5 p-2 border border-gray-200 rounded-sm bg-white min-h-[40px] cursor-text"
            onClick={() => ref.current?.focus()}
        >
            {values.map((v) => (
                <span key={v} className="flex items-center gap-1 px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-700">
                    {v}
                    <button type="button" onClick={(e) => { e.stopPropagation(); onChange(values.filter(x => x !== v)); }}>
                        <X className="w-3 h-3 hover:text-black" />
                    </button>
                </span>
            ))}
            <input
                ref={ref} value={input} onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKey} onBlur={add}
                placeholder={values.length === 0 ? placeholder : ""}
                className="flex-1 min-w-[120px] text-xs outline-none bg-transparent placeholder-gray-400"
            />
        </div>
    );
}

// ─── ToggleGroup ──────────────────────────────────────────────────────────────

function ToggleGroup({ options, values, onChange }: {
    options: { value: string; label: string }[];
    values: string[];
    onChange: (v: string[]) => void;
}) {
    const toggle = (v: string) =>
        onChange(values.includes(v) ? values.filter(x => x !== v) : [...values, v]);

    return (
        <div className="flex flex-wrap gap-2">
            {options.map((opt) => (
                <button
                    key={opt.value} type="button" onClick={() => toggle(opt.value)}
                    className={`px-3 py-1 rounded-sm text-xs border transition-colors ${
                        values.includes(opt.value)
                            ? "bg-black text-white border-black"
                            : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
                    }`}
                >
                    {opt.label}
                </button>
            ))}
        </div>
    );
}

// ─── TagSelector ──────────────────────────────────────────────────────────────

function TagSelector({ allTags, values, onChange, placeholder }: {
    allTags: string[]; values: string[]; onChange: (v: string[]) => void; placeholder: string;
}) {
    const [query, setQuery] = useState("");
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const filtered = allTags
        .filter(t => !values.includes(t) && t.toLowerCase().includes(query.toLowerCase()))
        .slice(0, 30);

    const toggle = (t: string) =>
        onChange(values.includes(t) ? values.filter(x => x !== t) : [...values, t]);

    return (
        <div ref={ref} className="relative">
            <div
                className="flex flex-wrap gap-1.5 p-2 border border-gray-200 rounded-sm bg-white min-h-[40px] cursor-text"
                onClick={() => setOpen(true)}
            >
                {values.map((v) => (
                    <span key={v} className="flex items-center gap-1 px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-700">
                        {v}
                        <button type="button" onClick={(e) => { e.stopPropagation(); toggle(v); }}>
                            <X className="w-3 h-3 hover:text-black" />
                        </button>
                    </span>
                ))}
                <input
                    value={query}
                    onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
                    onFocus={() => setOpen(true)}
                    placeholder={values.length === 0 ? placeholder : ""}
                    className="flex-1 min-w-[140px] text-xs outline-none bg-transparent placeholder-gray-400"
                />
            </div>
            {open && filtered.length > 0 && (
                <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-sm shadow-md max-h-48 overflow-y-auto">
                    {filtered.map((t) => (
                        <button
                            key={t} type="button"
                            onClick={() => { toggle(t); setQuery(""); }}
                            className="w-full text-left px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
                        >
                            {t}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

// ─── NafSelector ──────────────────────────────────────────────────────────────
// Replaces the old "types d'établissement" toggles: our own tags aren't precise
// enough, the NAF code is the reliable activity filter.

function NafSelector({ options, values, onChange }: {
    options: NafCodeOption[]; values: string[]; onChange: (v: string[]) => void;
}) {
    const [query, setQuery] = useState("");
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const labelOf = (code: string) => options.find(o => o.code === code)?.description;
    const q = query.toLowerCase();
    const filtered = options
        .filter(o => !values.includes(o.code))
        .filter(o => o.code.toLowerCase().includes(q) || (o.description ?? "").toLowerCase().includes(q))
        .slice(0, 40);

    return (
        <div ref={ref} className="relative">
            <div
                className="flex flex-wrap gap-1.5 p-2 border border-gray-200 rounded-sm bg-white min-h-[40px] cursor-text"
                onClick={() => setOpen(true)}
            >
                {values.map((code) => (
                    <span key={code} className="flex items-center gap-1 px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-700">
                        <span className="font-mono">{code}</span>
                        {labelOf(code) && <span className="text-gray-500 max-w-[220px] truncate">{labelOf(code)}</span>}
                        <button type="button" onClick={(e) => { e.stopPropagation(); onChange(values.filter(c => c !== code)); }}>
                            <X className="w-3 h-3 hover:text-black" />
                        </button>
                    </span>
                ))}
                <input
                    value={query}
                    onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
                    onFocus={() => setOpen(true)}
                    placeholder={values.length === 0 ? "Code NAF ou libellé (56.10A, boulangerie…)" : ""}
                    className="flex-1 min-w-[180px] text-xs outline-none bg-transparent placeholder-gray-400"
                />
            </div>
            {open && filtered.length > 0 && (
                <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-sm shadow-md max-h-56 overflow-y-auto">
                    {filtered.map((o) => (
                        <button
                            key={o.code} type="button"
                            onClick={() => { onChange([...values, o.code]); setQuery(""); }}
                            className="w-full text-left px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                        >
                            <span className="font-mono text-gray-900 shrink-0">{o.code}</span>
                            <span className="text-gray-500 truncate">{o.description}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

// ─── RegionSelect ─────────────────────────────────────────────────────────────

function RegionSelect({ values, onChange }: { values: string[]; onChange: (v: string[]) => void; }) {
    const toggle = (r: string) =>
        onChange(values.includes(r) ? values.filter(x => x !== r) : [...values, r]);

    return (
        <div className="flex flex-wrap gap-2">
            {FRENCH_REGIONS.map((r) => (
                <button key={r} type="button" onClick={() => toggle(r)}
                    className={`px-2.5 py-1 rounded-sm text-xs border transition-colors ${
                        values.includes(r)
                            ? "bg-black text-white border-black"
                            : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
                    }`}
                >
                    {r}
                </button>
            ))}
        </div>
    );
}

// ─── EstablishmentSearch ──────────────────────────────────────────────────────

function EstablishmentSearch({ onAdd, alreadyAdded, context }: {
    onAdd: (est: WeeklyEstablishment) => void;
    alreadyAdded: Set<string>;
    /** the send this search feeds — makes each row report its six-month block */
    context?: { draftId?: string; profileId?: string };
}) {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState<WeeklyEstablishment[]>([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    // Held in a ref so a new object identity from the parent's render does not restart the
    // debounce and fire a second search for a query that has not changed.
    const contextRef = useRef(context);
    contextRef.current = context;

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    useEffect(() => {
        if (timerRef.current) clearTimeout(timerRef.current);
        if (query.trim().length < 2) { setResults([]); setOpen(false); return; }
        timerRef.current = setTimeout(async () => {
            setLoading(true);
            try {
                const data = await searchEstablishments(query.trim(), contextRef.current);
                setResults(data);
                setOpen(true);
            } catch {
                setResults([]);
            } finally {
                setLoading(false);
            }
        }, 300);
    }, [query]);

    return (
        <div ref={containerRef} className="relative">
            <div className="flex items-center gap-2 border border-gray-200 rounded-sm px-3 py-2 bg-white focus-within:border-gray-400">
                {loading
                    ? <Loader2 className="w-4 h-4 text-gray-400 animate-spin shrink-0" />
                    : <Search className="w-4 h-4 text-gray-400 shrink-0" />
                }
                <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onFocus={() => results.length > 0 && setOpen(true)}
                    placeholder="SIREN, SIRET, nom, enseigne, propriétaire…"
                    className="flex-1 text-sm outline-none bg-transparent placeholder-gray-400"
                />
                {query && (
                    <button type="button" onClick={() => { setQuery(""); setResults([]); setOpen(false); }}>
                        <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                    </button>
                )}
            </div>
            {open && results.length > 0 && (
                <div className="absolute z-30 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-sm shadow-md max-h-64 overflow-y-auto">
                    {results.map((est) => {
                        const added = alreadyAdded.has(String(est._id));
                        const addr = [est.address?.postcode, est.address?.city].filter(Boolean).join(" ");
                        const sub = [est.type, addr].filter(Boolean).join(" · ");
                        return (
                            <div key={String(est._id)} className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 border-b border-gray-100 last:border-0">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-sm font-medium text-gray-900 truncate">{est.name}</span>
                                        <BlockedBadge est={est} />
                                    </div>
                                    {sub && <div className="text-xs text-gray-400 truncate">{sub}</div>}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => { if (!added) { onAdd(est); } }}
                                    disabled={added}
                                    title={added ? "Déjà dans la liste" : "Ajouter"}
                                    className={`shrink-0 w-6 h-6 flex items-center justify-center rounded border transition-colors ${
                                        added
                                            ? "border-green-200 bg-green-50 text-green-600 cursor-default"
                                            : "border-gray-200 text-gray-400 hover:border-black hover:text-black"
                                    }`}
                                >
                                    {added ? <Check className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ─── SiretAdder ───────────────────────────────────────────────────────────────
// Paste SIRET/SIREN straight from a document: any separator, several at once.

function SiretAdder({ onResolved, alreadyAdded, context }: {
    onResolved: (ests: WeeklyEstablishment[]) => void | Promise<void>;
    alreadyAdded: Set<string>;
    context?: { draftId?: string; profileId?: string };
}) {
    const [codes, setCodes] = useState("");
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState<{ kind: "ok" | "warn"; text: string } | null>(null);

    const submit = async () => {
        if (!codes.trim()) return;
        setBusy(true);
        setMessage(null);
        try {
            const { found, missing } = await findEstablishmentsBySiret(codes, context);
            // Blocked codes are reported by name instead of being pushed: pasting a list is a
            // bulk gesture, and silently dropping part of it would go unnoticed.
            //
            // Withheld only when the target is a send that is about to leave. Pinning one on a
            // profile is a standing instruction — the block may well have lapsed by the time
            // that profile next runs — so there it is flagged and added.
            const blocked = found.filter((e) => e.resendBlockReason);
            const withhold = Boolean(context?.draftId);
            const fresh = found.filter(
                (e) => !alreadyAdded.has(e._id) && !(withhold && e.resendBlockReason)
            );
            if (fresh.length > 0) await onResolved(fresh);

            const alreadyThere = found.length - fresh.length - (withhold ? blocked.length : 0);
            const parts: string[] = [];
            if (fresh.length > 0) parts.push(`${fresh.length} ajouté${fresh.length > 1 ? "s" : ""}`);
            if (alreadyThere > 0) parts.push(`${alreadyThere} déjà présent${alreadyThere > 1 ? "s" : ""}`);
            if (blocked.length > 0) {
                parts.push(
                    `${withhold ? "écarté" : "signalé"}${blocked.length > 1 ? "s" : ""}, déjà envoyé${blocked.length > 1 ? "s" : ""} : ${blocked.map((e) => e.name).join(", ")}`
                );
            }
            if (missing.length > 0) parts.push(`introuvable${missing.length > 1 ? "s" : ""} : ${missing.join(", ")}`);
            setMessage({
                kind: missing.length > 0 || blocked.length > 0 ? "warn" : "ok",
                text: parts.join(" · ") || "Aucun résultat",
            });
            if (missing.length === 0) setCodes("");
        } catch (err: any) {
            setMessage({ kind: "warn", text: err?.response?.data?.error ?? "Recherche impossible." });
        } finally {
            setBusy(false);
        }
    };

    return (
        <div>
            <div className="flex items-start gap-2">
                <textarea
                    value={codes}
                    onChange={(e) => setCodes(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void submit(); } }}
                    rows={2}
                    placeholder="12345678900012, 987654321… (SIRET 14 chiffres ou SIREN 9 chiffres, séparés par des virgules ou des retours à la ligne)"
                    className="flex-1 border border-gray-200 rounded-sm px-3 py-2 text-xs outline-none focus:border-gray-400 resize-y"
                />
                <button
                    type="button" onClick={submit} disabled={busy || !codes.trim()}
                    className="shrink-0 flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-sm text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
                >
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Hash className="w-3.5 h-3.5" />}
                    Ajouter
                </button>
            </div>
            {message && (
                <p className={`text-[11px] mt-1.5 ${message.kind === "ok" ? "text-emerald-600" : "text-amber-700"}`}>
                    {message.text}
                </p>
            )}
        </div>
    );
}

// ─── Section ──────────────────────────────────────────────────────────────────

function Section({ icon: Icon, title, children }: {
    icon: React.ElementType; title: string; children: React.ReactNode;
}) {
    return (
        <div className="border border-gray-200 rounded-sm p-4">
            <div className="flex items-center gap-2 mb-3">
                <Icon className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">{title}</span>
            </div>
            {children}
        </div>
    );
}

// ─── PinnedRow ────────────────────────────────────────────────────────────────

function PinnedRow({ est, onDelete }: { est: WeeklyEstablishment; onDelete: () => void }) {
    const [expanded, setExpanded] = useState(false);
    const address = [
        est.address?.street,
        est.address?.postcode && est.address?.city ? `${est.address.postcode} ${est.address.city}` : est.address?.city,
    ].filter(Boolean).join(", ");

    return (
        <div className="border border-gray-200 rounded-sm bg-white">
            <div className="flex items-start gap-3 p-3 cursor-pointer hover:bg-gray-50" onClick={() => setExpanded(v => !v)}>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-gray-900 truncate">{est.name}</span>
                        {est.type && <span className="px-1.5 py-0.5 bg-gray-100 text-gray-500 text-[10px] rounded">{est.type}</span>}
                        <BlockedBadge est={est} />
                        {(est.googleRating !== null || est.googleReviews !== null) && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-50 text-amber-700 text-[10px] rounded">
                                <Star className="w-2.5 h-2.5 fill-amber-500 text-amber-500" />
                                {est.googleRating !== null ? est.googleRating.toFixed(1).replace(".", ",") : "—"}
                                {est.googleReviews !== null && ` · ${est.googleReviews} avis`}
                            </span>
                        )}
                    </div>
                    {est.owner && (
                        <div className="flex items-center gap-1 mt-1 text-[11px] text-gray-500">
                            <Users className="w-3 h-3 shrink-0" />
                            <span className="truncate">
                                {[
                                    est.owner.name,
                                    est.owner.age !== null ? `${est.owner.age} ans` : null,
                                    `${est.owner.establishmentCount} établissement${est.owner.establishmentCount > 1 ? "s" : ""}`,
                                ].filter(Boolean).join(" · ")}
                            </span>
                        </div>
                    )}
                    <div className="flex flex-wrap gap-1 mt-1">
                        {est.signals.slice(0, 3).map((s, i) => (
                            <span key={i} className="px-2 py-0.5 bg-emerald-50 text-emerald-700 text-[10px] rounded-full border border-emerald-100">
                                {s.label} · {fmtDate(s.date)}
                            </span>
                        ))}
                    </div>
                </div>
                <div className="text-xs text-gray-500 text-right shrink-0 hidden sm:block max-w-[240px] truncate">
                    {address || "—"}
                </div>
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onDelete(); }}
                    className="shrink-0 p-0.5 text-gray-300 hover:text-red-500 transition-colors"
                    title="Retirer"
                >
                    <X className="w-4 h-4" />
                </button>
                <div className="shrink-0 text-gray-400">
                    {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </div>
            </div>
            {expanded && (
                <div className="px-3 pb-3 border-t border-gray-100 pt-3 grid grid-cols-2 gap-x-6 gap-y-2 text-xs">
                    <div><span className="text-gray-400">Téléphone </span><span className="text-gray-700">{est.phone || "—"}</span></div>
                    <div><span className="text-gray-400">Email </span><span className="text-gray-700">{est.email || "—"}</span></div>
                    {est.siret && (
                        <div><span className="text-gray-400">SIRET </span><span className="text-gray-700 font-mono">{est.siret}</span></div>
                    )}
                    {est.googleUrl && (
                        <div>
                            <a
                                href={est.googleUrl} target="_blank" rel="noopener noreferrer"
                                className="text-blue-600 hover:underline inline-flex items-center gap-1"
                            >
                                <ExternalLink className="w-3 h-3" /> Fiche Google
                            </a>
                        </div>
                    )}
                    {est.nafCode && (
                        <div className="col-span-2">
                            <span className="text-gray-400">NAF </span>
                            <span className="text-gray-700 font-mono">{est.nafCode}</span>
                            {est.nafLabel && <span className="text-gray-500"> — {est.nafLabel}</span>}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ─── Profile list ─────────────────────────────────────────────────────────────

function ProfileList({ onOpen, onOpenDetail, onOpenDraft, onOpenFeedback }: {
    onOpen: (id: string | null) => void;
    onOpenDetail: (id: string) => void;
    onOpenDraft: (id: string) => void;
    onOpenFeedback: (id: string) => void;
}) {
    const [profiles, setProfiles] = useState<WeeklyMailProfile[] | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [busyId, setBusyId] = useState<string | null>(null);

    const reload = useCallback(async () => {
        try {
            setProfiles(await listProfiles());
        } catch (err: any) {
            setError(err?.response?.data?.error ?? "Impossible de charger les profils.");
        }
    }, []);

    useEffect(() => { void reload(); }, [reload]);

    const toggleActive = async (p: WeeklyMailProfile) => {
        setBusyId(p._id);
        try {
            await updateProfile(p._id, { active: !p.active });
            await reload();
        } catch (err: any) {
            setError(err?.response?.data?.error ?? "Mise à jour impossible.");
        } finally {
            setBusyId(null);
        }
    };

    const remove = async (p: WeeklyMailProfile) => {
        if (!window.confirm(`Supprimer le profil « ${p.name} » et ses aperçus en attente ?`)) return;
        setBusyId(p._id);
        try {
            await deleteProfile(p._id);
            await reload();
        } catch (err: any) {
            setError(err?.response?.data?.error ?? "Suppression impossible.");
        } finally {
            setBusyId(null);
        }
    };

    const generateNow = async (p: WeeklyMailProfile) => {
        setBusyId(p._id);
        try {
            const draft = await prepareDraft(p._id);
            onOpenDraft(draft._id);
        } catch (err: any) {
            setError(err?.response?.data?.error ?? "Génération impossible.");
        } finally {
            setBusyId(null);
        }
    };

    return (
        <div>
            <div className="flex items-start justify-between mb-6">
                <div>
                    <h1 className="text-xl font-semibold text-gray-900">Mails hebdomadaires</h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Un profil = un mailing récurrent : ses destinataires, son créneau d'envoi et ses propres critères de sélection.
                        L'aperçu est préparé {PREVIEW_LEAD_DAYS} jours avant chaque envoi et reste modifiable jusqu'au départ.
                    </p>
                </div>
                <button
                    onClick={() => onOpen(null)}
                    className="shrink-0 flex items-center gap-2 px-4 py-2.5 bg-black text-white text-sm font-medium rounded-sm hover:bg-gray-800 transition-colors"
                >
                    <Plus className="w-4 h-4" />
                    Nouveau profil
                </button>
            </div>

            {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-sm text-sm text-red-700">{error}</div>
            )}

            {profiles === null ? (
                <div className="flex items-center gap-2 text-sm text-gray-400 p-6">
                    <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
                </div>
            ) : profiles.length === 0 ? (
                <div className="border border-dashed border-gray-300 rounded-sm p-10 text-center">
                    <Mail className="w-6 h-6 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-500">Aucun profil pour le moment.</p>
                    <button onClick={() => onOpen(null)} className="mt-3 text-sm font-medium text-black underline">
                        Créer le premier mailing
                    </button>
                </div>
            ) : (
                <div className="grid gap-3">
                    {profiles.map((p) => {
                        const draft = p.upcomingDraft;
                        return (
                            <div key={p._id} className="border border-gray-200 rounded-sm bg-white p-4">
                                <div className="flex items-start gap-4">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <button onClick={() => onOpenDetail(p._id)} className="text-sm font-semibold text-gray-900 hover:underline">
                                                {p.name}
                                            </button>
                                            <span className={`px-1.5 py-0.5 text-[10px] rounded ${p.active ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
                                                {p.active ? "Actif" : "En pause"}
                                            </span>
                                            {draft && (
                                                <span className={`px-1.5 py-0.5 text-[10px] rounded ${draft.edited ? "bg-violet-50 text-violet-700" : "bg-amber-50 text-amber-700"}`}>
                                                    Aperçu prêt · {draft.cardCount} établissement{draft.cardCount > 1 ? "s" : ""}{draft.edited ? " · modifié" : ""}
                                                </span>
                                            )}
                                        </div>
                                        {p.description && <p className="text-xs text-gray-500 mt-1">{p.description}</p>}
                                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500 flex-wrap">
                                            <span className="inline-flex items-center gap-1">
                                                <Clock className="w-3 h-3" /> {scheduleLabel(p.schedule)}
                                            </span>
                                            <span className="inline-flex items-center gap-1">
                                                <Users className="w-3 h-3" /> {p.recipients.length} destinataire{p.recipients.length > 1 ? "s" : ""}
                                            </span>
                                            {p.nextSendAt && (
                                                <span className="inline-flex items-center gap-1">
                                                    <Send className="w-3 h-3" /> Prochain envoi {fmtDateTime(p.nextSendAt)}
                                                    {daysUntil(p.nextSendAt) >= 0 && ` (J-${daysUntil(p.nextSendAt)})`}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        <button
                                            onClick={() => onOpenDetail(p._id)}
                                            title="Le brief à venir et tout ce qui a déjà été envoyé"
                                            className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-sm text-xs text-gray-700 hover:bg-gray-50 transition-colors"
                                        >
                                            <Eye className="w-3.5 h-3.5" /> Suivi
                                        </button>
                                        {draft ? (
                                            <button
                                                onClick={() => onOpenDraft(draft._id)}
                                                className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-sm text-xs text-gray-600 hover:bg-gray-50 transition-colors"
                                            >
                                                <PencilLine className="w-3.5 h-3.5" /> Envoi
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => generateNow(p)}
                                                disabled={busyId === p._id}
                                                className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-sm text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition-colors"
                                            >
                                                {busyId === p._id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                                                Préparer
                                            </button>
                                        )}
                                        <button
                                            onClick={() => onOpenFeedback(p._id)}
                                            title="Avis des destinataires"
                                            className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-sm text-xs text-gray-600 hover:bg-gray-50 transition-colors"
                                        >
                                            <MessageSquare className="w-3.5 h-3.5" /> Avis
                                        </button>
                                        <button
                                            onClick={() => onOpen(p._id)}
                                            className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-sm text-xs text-gray-600 hover:bg-gray-50 transition-colors"
                                        >
                                            <PencilLine className="w-3.5 h-3.5" /> Modifier
                                        </button>
                                        <button
                                            onClick={() => toggleActive(p)}
                                            disabled={busyId === p._id}
                                            title={p.active ? "Mettre en pause" : "Réactiver"}
                                            className="p-1.5 border border-gray-200 rounded-sm text-gray-500 hover:bg-gray-50 disabled:opacity-50 transition-colors"
                                        >
                                            <Power className="w-3.5 h-3.5" />
                                        </button>
                                        <button
                                            onClick={() => remove(p)}
                                            disabled={busyId === p._id}
                                            title="Supprimer"
                                            className="p-1.5 border border-gray-200 rounded-sm text-gray-400 hover:text-red-500 hover:border-red-200 disabled:opacity-50 transition-colors"
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ─── Profile editor ───────────────────────────────────────────────────────────

function ProfileEditor({ profileId, onBack, onOpenDraft, onCreated }: {
    profileId: string | null;
    onBack: () => void;
    onOpenDraft: (id: string) => void;
    onCreated: (id: string) => void;
}) {
    // Once created, every later save must update that profile — otherwise a
    // second click on "Enregistrer" creates a duplicate.
    const [currentId, setCurrentId] = useState<string | null>(profileId);
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [customTitle, setCustomTitle] = useState("Briefing hebdomadaire CHR");
    const [active, setActive] = useState(true);
    const [recipients, setRecipients] = useState<WeeklyMailRecipient[]>([]);
    const [schedule, setSchedule] = useState<WeeklyMailSchedule>({
        dayOfWeek: 1, hour: 8, minute: 0, times: [{ dayOfWeek: 1, hour: 8, minute: 0 }], occurrences: null, sentCount: 0,
    });
    // In the order they were entered while editing — re-sorted on each keystroke, a row would
    // jump away from under the cursor as soon as its day changed.
    const slots: WeeklyMailTimeSlot[] = schedule.times?.length
        ? schedule.times
        : [{ dayOfWeek: schedule.dayOfWeek, hour: schedule.hour, minute: schedule.minute }];
    const setSlots = (times: WeeklyMailTimeSlot[]) => setSchedule((prev) => ({ ...prev, times }));
    const [filters, setFilters] = useState<WeeklyMailProfileFilters>(EMPTY_FILTERS);
    const [pinned, setPinned] = useState<WeeklyEstablishment[]>([]);

    const [allCategories, setAllCategories] = useState<string[]>([]);
    const [allSubCategories, setAllSubCategories] = useState<string[]>([]);
    const [allNafCodes, setAllNafCodes] = useState<NafCodeOption[]>([]);

    const [loading, setLoading] = useState(profileId !== null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [saved, setSaved] = useState(false);
    const [previewing, setPreviewing] = useState(false);
    const [preview, setPreview] = useState<{ html: string; subject: string } | null>(null);

    useEffect(() => {
        getEstablishmentTags()
            .then(({ categories, subCategories }) => {
                setAllCategories(categories.sort());
                setAllSubCategories(subCategories.sort());
            })
            .catch(() => {});
        getUniqueNafCodes().then((c) => setAllNafCodes(c ?? [])).catch(() => {});
    }, []);

    useEffect(() => {
        if (!profileId) return;
        let cancelled = false;
        (async () => {
            try {
                const { profile } = await getProfile(profileId);
                if (cancelled) return;
                setName(profile.name);
                setDescription(profile.description ?? "");
                setCustomTitle(profile.customTitle);
                setActive(profile.active);
                setRecipients(profile.recipients);
                setSchedule({ ...profile.schedule, times: scheduleSlots(profile.schedule) });
                setFilters({
                    ...EMPTY_FILTERS,
                    ...profile.filters,
                    // A profile saved under the floor shows what it will actually send.
                    maxEstablishments: Math.max(MIN_ESTABLISHMENTS, profile.filters.maxEstablishments ?? 7),
                });
                if (profile.filters.manualIds?.length) {
                    const ests = await getEstablishmentsByIds(profile.filters.manualIds);
                    if (!cancelled) setPinned(ests);
                }
            } catch (err: any) {
                if (!cancelled) setError(err?.response?.data?.error ?? "Profil introuvable.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [profileId]);

    const patchFilters = (patch: Partial<WeeklyMailProfileFilters>) =>
        setFilters((prev) => ({ ...prev, ...patch }));

    const payload = (): Partial<WeeklyMailProfile> => {
        const sorted = scheduleSlots({ ...schedule, times: slots });
        return {
            name: name.trim(),
            description: description.trim(),
            customTitle: customTitle.trim() || "Briefing hebdomadaire CHR",
            active,
            recipients,
            // The first slot is mirrored into the legacy single day/hour, as the server does.
            schedule: { ...schedule, times: sorted, dayOfWeek: sorted[0].dayOfWeek, hour: sorted[0].hour, minute: sorted[0].minute },
            filters: { ...filters, manualIds: pinned.map((e) => e._id) },
        };
    };

    const save = async (): Promise<string | null> => {
        if (!name.trim()) { setError("Donnez un nom au profil."); return null; }
        if (saving) return null;
        setSaving(true);
        setError(null);
        try {
            const profile = currentId
                ? await updateProfile(currentId, payload())
                : await createProfile(payload());
            if (!currentId) {
                setCurrentId(profile._id);
                onCreated(profile._id);
            }
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
            return profile._id;
        } catch (err: any) {
            setError(err?.response?.data?.error ?? "Enregistrement impossible.");
            return null;
        } finally {
            setSaving(false);
        }
    };

    // Saves first: the draft is always built from the persisted profile. Never
    // forces — an already prepared brief keeps the edits made on it, and the
    // next occurrence is rebuilt from scratch PREVIEW_LEAD_DAYS before it goes out.
    const saveAndPrepare = async () => {
        const id = await save();
        if (!id) return;
        setPreviewing(true);
        try {
            const draft = await prepareDraft(id, false);
            onOpenDraft(draft._id);
        } catch (err: any) {
            setError(err?.response?.data?.error ?? "Génération impossible.");
        } finally {
            setPreviewing(false);
        }
    };

    // Quick look without touching the schedule or creating a draft
    const quickPreview = async () => {
        setPreviewing(true);
        setError(null);
        try {
            const to = new Date();
            const from = new Date(Date.now() - filters.lookbackDays * 86400000);
            const result = await generateWeeklyMail({
                cities: filters.cities,
                regions: filters.regions,
                departments: filters.departments,
                nafCodes: filters.nafCodes,
                signalTypes: filters.signalTypes,
                categories: filters.categories,
                subCategories: filters.subCategories,
                manualIds: pinned.map((e) => e._id),
                maxEstablishments: filters.maxEstablishments,
                slots: filters.slots,
                customTitle: customTitle.trim() || undefined,
                // Without the audience the preview would show establishments the real send
                // then drops on the six-month block — an aperçu has to match what goes out.
                recipientEmails: recipients.map((r) => r.email),
                dateFrom: from.toISOString().slice(0, 10),
                dateTo: to.toISOString().slice(0, 10),
            });
            setPreview({ html: result.html, subject: result.subject });
        } catch (err: any) {
            setError(err?.response?.data?.error ?? "Aperçu impossible.");
        } finally {
            setPreviewing(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center gap-2 text-sm text-gray-400 p-6">
                <Loader2 className="w-4 h-4 animate-spin" /> Chargement du profil…
            </div>
        );
    }

    return (
        <div>
            <button onClick={onBack} className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-black mb-4">
                <ArrowLeft className="w-3.5 h-3.5" /> Tous les profils
            </button>

            <div className="mb-6">
                <h1 className="text-xl font-semibold text-gray-900">
                    {profileId ? "Modifier le profil" : "Nouveau profil de mailing"}
                </h1>
                <p className="text-sm text-gray-500 mt-1">
                    Chaque profil a ses propres destinataires, son créneau et ses critères.
                </p>
            </div>

            <div className="grid gap-4">
                {/* Identity */}
                <Section icon={Type} title="Identité du mailing">
                    <div className="grid gap-3">
                        <div>
                            <label className="text-xs text-gray-500 mb-1 block">Nom interne</label>
                            <input
                                value={name} onChange={(e) => setName(e.target.value)}
                                placeholder="Ex : Clients Île-de-France — ouvertures"
                                className="w-full border border-gray-200 rounded-sm px-3 py-2 text-sm outline-none focus:border-gray-400"
                            />
                        </div>
                        <div>
                            <label className="text-xs text-gray-500 mb-1 block">Titre du briefing (objet du mail)</label>
                            <input
                                value={customTitle} onChange={(e) => setCustomTitle(e.target.value)}
                                className="w-full border border-gray-200 rounded-sm px-3 py-2 text-sm outline-none focus:border-gray-400"
                            />
                        </div>
                        <div>
                            <label className="text-xs text-gray-500 mb-1 block">Note interne (facultatif)</label>
                            <input
                                value={description} onChange={(e) => setDescription(e.target.value)}
                                className="w-full border border-gray-200 rounded-sm px-3 py-2 text-sm outline-none focus:border-gray-400"
                            />
                        </div>
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} className="w-3.5 h-3.5 accent-black" />
                            <span className="text-xs text-gray-700">Profil actif (les envois programmés partent automatiquement)</span>
                        </label>
                    </div>
                </Section>

                {/* Recipients */}
                <Section icon={Users} title="Destinataires">
                    <p className="text-xs text-gray-400 mb-2">Une adresse par entrée. Chacun reçoit son propre exemplaire (les avis restent attribuables).</p>
                    <ChipInput
                        values={recipients.map((r) => r.email)}
                        onChange={(emails) => setRecipients(emails.map((email) => ({ email })))}
                        placeholder="email@client.fr + Entrée"
                    />
                </Section>

                {/* Schedule */}
                <Section icon={Clock} title="Programmation">
                    <div className="grid gap-2 mb-4">
                        <label className="text-xs text-gray-500">
                            Créneaux d'envoi chaque semaine — autant que nécessaire, y compris plusieurs le même jour
                        </label>
                        {slots.map((slot, i) => (
                            <div key={i} className="flex items-center gap-2">
                                <select
                                    value={slot.dayOfWeek}
                                    onChange={(e) => setSlots(slots.map((s, j) => (j === i ? { ...s, dayOfWeek: Number(e.target.value) } : s)))}
                                    className="border border-gray-200 rounded-sm px-2 py-1.5 text-sm outline-none focus:border-gray-400"
                                >
                                    {/* Monday first, the way the week is read. */}
                                    {[1, 2, 3, 4, 5, 6, 0].map((d) => <option key={d} value={d}>{DAYS[d]}</option>)}
                                </select>
                                <input
                                    type="time"
                                    value={hhmm(slot.hour, slot.minute)}
                                    onChange={(e) => {
                                        const [h, m] = e.target.value.split(":").map(Number);
                                        setSlots(slots.map((s, j) => (j === i ? { ...s, hour: h || 0, minute: m || 0 } : s)));
                                    }}
                                    className="border border-gray-200 rounded-sm px-2 py-1.5 text-sm outline-none focus:border-gray-400"
                                />
                                <button
                                    type="button"
                                    onClick={() => setSlots(slots.filter((_, j) => j !== i))}
                                    disabled={slots.length === 1}
                                    title={slots.length === 1 ? "Un profil garde au moins un créneau" : "Retirer ce créneau"}
                                    className="p-1 text-gray-300 hover:text-red-500 disabled:opacity-30 disabled:hover:text-gray-300 transition-colors"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        ))}
                        <div className="flex items-center gap-3">
                            <button
                                type="button"
                                onClick={() => {
                                    // Starts from the last slot, a day later: the usual next step is "and on Friday too".
                                    const last = slots[slots.length - 1];
                                    setSlots([...slots, { ...last, dayOfWeek: (last.dayOfWeek + 1) % 7 }]);
                                }}
                                disabled={slots.length >= 21}
                                className="flex items-center gap-1.5 px-2.5 py-1.5 border border-gray-200 rounded-sm text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition-colors"
                            >
                                <Plus className="w-3.5 h-3.5" /> Ajouter un créneau
                            </button>
                            <span className="text-xs text-gray-400">
                                {slots.length} envoi{slots.length > 1 ? "s" : ""} par semaine
                            </span>
                        </div>
                    </div>
                    <div className="flex items-end gap-4 flex-wrap">
                        <div className="flex flex-col gap-1">
                            <label className="text-xs text-gray-500">Nombre d'envois</label>
                            <div className="flex items-center gap-2">
                                <label className="flex items-center gap-1.5 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={schedule.occurrences === null}
                                        onChange={(e) => setSchedule({ ...schedule, occurrences: e.target.checked ? null : 12 })}
                                        className="w-3.5 h-3.5 accent-black"
                                    />
                                    <span className="text-xs text-gray-700">Illimité</span>
                                </label>
                                {schedule.occurrences !== null && (
                                    <input
                                        type="number" min={1} value={schedule.occurrences}
                                        onChange={(e) => setSchedule({ ...schedule, occurrences: Math.max(1, Number(e.target.value) || 1) })}
                                        className="w-20 border border-gray-200 rounded-sm px-2 py-1.5 text-sm outline-none focus:border-gray-400"
                                    />
                                )}
                            </div>
                        </div>
                        {schedule.sentCount > 0 && (
                            <span className="text-xs text-gray-400 pb-2">{schedule.sentCount} envoi{schedule.sentCount > 1 ? "s" : ""} déjà effectué{schedule.sentCount > 1 ? "s" : ""}</span>
                        )}
                    </div>
                    <p className="text-xs text-gray-400 mt-3">
                        L'aperçu est généré {PREVIEW_LEAD_DAYS} jours avant chaque envoi ; vous pouvez tout modifier jusqu'au départ.
                        Depuis le calendrier du profil, chaque envoi peut aussi être déplacé ou sauté seul, et des envois ponctuels ajoutés.
                    </p>
                </Section>

                {/* Period */}
                <Section icon={Calendar} title="Période couverte">
                    <div className="flex items-center gap-3">
                        <label className="text-xs text-gray-500">Les</label>
                        <input
                            type="number" min={1} max={90} value={filters.lookbackDays}
                            onChange={(e) => patchFilters({ lookbackDays: Math.min(90, Math.max(1, Number(e.target.value) || 1)) })}
                            className="w-20 border border-gray-200 rounded-sm px-2 py-1.5 text-sm outline-none focus:border-gray-400"
                        />
                        <span className="text-xs text-gray-500">derniers jours avant chaque envoi.</span>
                    </div>
                </Section>

                {/* Location */}
                <Section icon={MapPin} title="Localisation">
                    <div className="grid gap-3">
                        <div>
                            <label className="text-xs text-gray-500 mb-1 block">Régions</label>
                            <RegionSelect values={filters.regions} onChange={(regions) => patchFilters({ regions })} />
                        </div>
                        <div>
                            <label className="text-xs text-gray-500 mb-1 block">
                                Départements <span className="text-gray-400">(codes : 75, 13, 69…)</span>
                            </label>
                            <ChipInput values={filters.departments} onChange={(departments) => patchFilters({ departments })} placeholder="Code département + Entrée" />
                        </div>
                        <div>
                            <label className="text-xs text-gray-500 mb-1 block">Villes</label>
                            <ChipInput values={filters.cities} onChange={(cities) => patchFilters({ cities })} placeholder="Ville + Entrée" />
                        </div>
                    </div>
                </Section>

                {/* NAF */}
                <Section icon={Store} title="Code NAF">
                    <p className="text-xs text-gray-400 mb-2">
                        Filtre d'activité de référence (nos tags internes ne sont pas assez précis). Laisser vide pour toutes les activités.
                    </p>
                    <NafSelector options={allNafCodes} values={filters.nafCodes} onChange={(nafCodes) => patchFilters({ nafCodes })} />
                </Section>

                {/* Tags */}
                <Section icon={Tag} title="Tags et sous-tags">
                    <div className="grid gap-3">
                        <div>
                            <label className="text-xs text-gray-500 mb-1 block">Catégories</label>
                            <TagSelector allTags={allCategories} values={filters.categories} onChange={(categories) => patchFilters({ categories })} placeholder="Rechercher une catégorie…" />
                        </div>
                        <div>
                            <label className="text-xs text-gray-500 mb-1 block">Sous-catégories</label>
                            <TagSelector allTags={allSubCategories} values={filters.subCategories} onChange={(subCategories) => patchFilters({ subCategories })} placeholder="Rechercher une sous-catégorie…" />
                        </div>
                    </div>
                </Section>

                {/* Signals */}
                <Section icon={Zap} title="Types de signaux">
                    <p className="text-xs text-gray-400 mb-2">Laisser vide pour tous.</p>
                    <ToggleGroup options={SIGNAL_TYPES} values={filters.signalTypes} onChange={(signalTypes) => patchFilters({ signalTypes })} />
                </Section>

                {/* Composition */}
                <Section icon={SlidersHorizontal} title="Composition du brief">
                    <div className="grid gap-3">
                        <div className="flex items-center gap-3">
                            <label className="text-xs text-gray-500">Nombre d'établissements dans le mail</label>
                            <input
                                type="number" min={MIN_ESTABLISHMENTS} max={20} value={filters.maxEstablishments}
                                onChange={(e) => patchFilters({ maxEstablishments: Math.min(20, Math.max(1, Number(e.target.value) || 1)) })}
                                // Clamped on blur rather than on each keystroke, or typing "12" would stop at "5".
                                onBlur={() => patchFilters({ maxEstablishments: Math.min(20, Math.max(MIN_ESTABLISHMENTS, filters.maxEstablishments)) })}
                                className="w-20 border border-gray-200 rounded-sm px-2 py-1.5 text-sm outline-none focus:border-gray-400"
                            />
                            <span className="text-[11px] text-gray-400">
                                minimum {MIN_ESTABLISHMENTS} : une semaine calme est complétée par des établissements de la zone
                            </span>
                        </div>
                        <div className="grid gap-2">
                            <p className="text-xs text-gray-500">Slots d'enrichissement (hors filtres de signaux)</p>
                            {([
                                { key: "hygiene" as const, label: "Contrôle sanitaire de la veille (1 à 2)", hint: "Garantit un établissement actif — souvent mieux enrichi." },
                                { key: "multiOwner" as const, label: "Dirigeant multi-établissements (2 à 10)", hint: "Raison affichée : « Compte à gros potentiel ». Exclut les envois de moins de 30 jours." },
                                { key: "richContacts" as const, label: "Coordonnées complètes (LinkedIn + email + tél + Insta)", hint: "Raison affichée : « Fort potentiel de réactivité détecté »." },
                            ]).map(({ key, label, hint }) => (
                                <label key={key} className="flex items-start gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={filters.slots[key]}
                                        onChange={(e) => patchFilters({ slots: { ...filters.slots, [key]: e.target.checked } })}
                                        className="mt-0.5 w-3.5 h-3.5 accent-black"
                                    />
                                    <span>
                                        <span className="text-xs text-gray-700">{label}</span>
                                        <span className="block text-[11px] text-gray-400">{hint}</span>
                                    </span>
                                </label>
                            ))}
                        </div>
                    </div>
                </Section>

                {/* Pinned establishments */}
                <Section icon={Search} title="Établissements épinglés">
                    <p className="text-xs text-gray-400 mb-2">
                        Toujours inclus dans les envois de ce profil, quels que soient les filtres.
                    </p>
                    <EstablishmentSearch
                        onAdd={(est) => setPinned((prev) => prev.some((e) => e._id === est._id) ? prev : [...prev, est])}
                        alreadyAdded={new Set(pinned.map((e) => e._id))}
                        context={profileId ? { profileId } : undefined}
                    />
                    <p className="text-xs text-gray-400 mt-3 mb-1.5">Ou par SIRET / SIREN</p>
                    <SiretAdder
                        alreadyAdded={new Set(pinned.map((e) => e._id))}
                        context={profileId ? { profileId } : undefined}
                        onResolved={(ests) => setPinned((prev) => [...prev, ...ests])}
                    />
                    {pinned.length > 0 && (
                        <div className="grid gap-2 mt-3">
                            {pinned.map((est) => (
                                <PinnedRow key={est._id} est={est} onDelete={() => setPinned((prev) => prev.filter((e) => e._id !== est._id))} />
                            ))}
                        </div>
                    )}
                </Section>
            </div>

            {error && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-sm text-sm text-red-700">{error}</div>
            )}

            <div className="mt-6 flex items-center gap-3 flex-wrap">
                <button
                    onClick={save} disabled={saving}
                    className="flex items-center gap-2 px-5 py-2.5 bg-black text-white text-sm font-medium rounded-sm hover:bg-gray-800 disabled:opacity-50 transition-colors"
                >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? <Check className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                    {saving ? "Enregistrement…" : saved ? "Enregistré" : "Enregistrer le profil"}
                </button>
                <button
                    onClick={quickPreview} disabled={previewing || saving}
                    className="flex items-center gap-2 px-4 py-2.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-sm hover:bg-gray-50 disabled:opacity-50 transition-colors"
                >
                    {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                    Aperçu rapide
                </button>
                <button
                    onClick={saveAndPrepare} disabled={previewing || saving}
                    className="flex items-center gap-2 px-4 py-2.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-sm hover:bg-gray-50 disabled:opacity-50 transition-colors"
                >
                    {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    Enregistrer et ouvrir l'aperçu du prochain envoi
                </button>
            </div>

            {preview && (
                <div className="mt-6">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">Aperçu rapide — {preview.subject}</span>
                        <button onClick={() => setPreview(null)} className="text-xs text-gray-400 hover:text-black">Fermer</button>
                    </div>
                    <iframe
                        srcDoc={previewDoc(preview.html)}
                        title="Aperçu rapide"
                        sandbox={PREVIEW_SANDBOX}
                        className="w-full border border-gray-200 rounded-sm"
                        style={{ height: "700px" }}
                    />
                </div>
            )}
        </div>
    );
}

// ─── Typology search ──────────────────────────────────────────────────────────

/**
 * "Trouve-moi des établissements comme ce type de client."
 *
 * Two sources of types. The saved ones come from `/client-types`, where you look at a portfolio
 * and recognise a pattern; the generated ones are read off *this* mail's current list, to find
 * more of what is already in it. Either way, picking a type fills the box with its sentence, and
 * that sentence stays editable — refining it for one brief must not mean editing the saved type.
 * Behind it is the same assistant the Database page's filter bar runs.
 *
 * At least ten rows come back when the zone holds them: a search that found fewer widens its
 * criteria one step at a time, and every widened row says so.
 */
function TypologySearch({ draftId, alreadyAdded, onAdd }: {
    draftId: string;
    alreadyAdded: Set<string>;
    onAdd: (est: WeeklyEstablishment) => void | Promise<void>;
}) {
    const [typologies, setTypologies] = useState<ClientTypology[]>([]);
    const [generated, setGenerated] = useState<WeeklyDraftTypology[] | null>(null);
    const [generating, setGenerating] = useState(false);
    // What the select shows: a saved id, or "gen:<index>" for a generated one.
    const [picked, setPicked] = useState("");
    const [prompt, setPrompt] = useState("");
    const [result, setResult] = useState<TypologySearchResult | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        listTypologies()
            .then(setTypologies)
            .catch(() => { /* free text still works without the saved list */ });
    }, []);

    const pick = (value: string) => {
        setPicked(value);
        // Copied into the box rather than kept as a hidden reference: what will be searched
        // has to be visible and editable before it runs.
        if (value.startsWith("gen:")) {
            const typology = generated?.[Number(value.slice(4))];
            setPrompt(typology ? [typology.searchPrompt, ...typology.defining].filter(Boolean).join(". ") : "");
            return;
        }
        const typology = typologies.find((t) => t._id === value);
        setPrompt(typology ? [typology.searchPrompt, ...typology.traits].filter(Boolean).join(". ") : "");
    };

    const generate = async () => {
        setGenerating(true);
        setError(null);
        try {
            const { profiles } = await getDraftTypologies(draftId);
            setGenerated(profiles);
            if (profiles.length === 0) setError("L'assistant n'a dégagé aucune typologie de cette liste.");
        } catch (err: any) {
            setError(err?.response?.data?.error ?? "Analyse de la liste impossible.");
        } finally {
            setGenerating(false);
        }
    };

    const run = async () => {
        setBusy(true);
        setError(null);
        try {
            setResult(await searchByTypology({
                // Only a saved type is sent by id (it bumps its usage counter); a generated one
                // exists nowhere but in this box.
                typologyId: picked && !picked.startsWith("gen:") ? picked : undefined,
                prompt: prompt.trim() || undefined,
                draftId,
                limit: 20,
            }));
        } catch (err: any) {
            setError(err?.response?.data?.error ?? "Recherche impossible.");
            setResult(null);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="grid gap-3">
            <div className="flex items-center gap-2 flex-wrap">
                <select
                    value={picked}
                    onChange={(e) => pick(e.target.value)}
                    className="border border-gray-200 rounded-sm px-2 py-2 text-xs outline-none focus:border-gray-400 bg-white min-w-[220px] max-w-[360px]"
                >
                    <option value="">Choisir une typologie…</option>
                    {generated && generated.length > 0 && (
                        <optgroup label="Générées depuis ce mail">
                            {generated.map((t, i) => (
                                <option key={`gen-${i}`} value={`gen:${i}`}>
                                    {t.label} ({t.count} établissement{t.count > 1 ? "s" : ""})
                                </option>
                            ))}
                        </optgroup>
                    )}
                    {typologies.length > 0 && (
                        <optgroup label="Enregistrées">
                            {typologies.map((t) => (
                                <option key={t._id} value={t._id}>
                                    {t.name}{t.useCount > 0 ? ` (${t.useCount})` : ""}
                                </option>
                            ))}
                        </optgroup>
                    )}
                </select>
                <button
                    type="button" onClick={generate} disabled={generating}
                    title="Analyse les établissements actuellement dans ce mail et en dégage des typologies à rechercher"
                    className="flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-sm text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
                >
                    {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                    {generated ? "Regénérer depuis la liste actuelle" : "Générer depuis la liste actuelle"}
                </button>
                {typologies.length === 0 && !generated && (
                    <span className="text-[11px] text-gray-400">
                        Aucune typologie enregistrée — créez-en depuis « Intelligence clients ».
                    </span>
                )}
            </div>

            {/* The generated types as chips too: a select hides what it offers until opened. */}
            {generated && generated.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                    {generated.map((t, i) => (
                        <button
                            key={i}
                            type="button"
                            onClick={() => pick(`gen:${i}`)}
                            title={[t.description, t.members.length ? `Dans le mail : ${t.members.join(", ")}` : ""].filter(Boolean).join("\n")}
                            className={`px-2 py-1 rounded-sm text-[11px] border transition-colors ${
                                picked === `gen:${i}`
                                    ? "bg-black text-white border-black"
                                    : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
                            }`}
                        >
                            {t.label} · {t.count}
                        </button>
                    ))}
                </div>
            )}

            <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={2}
                placeholder="Ou décrivez le type de client : « boulangeries indépendantes en Moselle, dirigeant de plus de 50 ans, note Google supérieure à 4 »"
                className="w-full border border-gray-200 rounded-sm px-3 py-2 text-xs outline-none focus:border-gray-400 resize-y"
            />

            <div className="flex items-center gap-2 flex-wrap">
                <button
                    type="button" onClick={run} disabled={busy || !prompt.trim()}
                    className="flex items-center gap-1.5 px-3 py-2 bg-black text-white rounded-sm text-xs font-medium hover:bg-gray-800 disabled:opacity-50 transition-colors"
                >
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
                    Chercher des établissements similaires
                </button>
                {result && (
                    <span className="text-[11px] text-gray-500">
                        {result.totalMatched} correspondance{result.totalMatched > 1 ? "s" : ""} stricte{result.totalMatched > 1 ? "s" : ""}
                        {result.blocked > 0 && ` · ${result.blocked} écarté${result.blocked > 1 ? "s" : ""} (déjà envoyé${result.blocked > 1 ? "s" : ""})`}
                        {` · ${result.establishments.length} proposé${result.establishments.length > 1 ? "s" : ""}`}
                    </span>
                )}
            </div>

            {error && <p className="text-[11px] text-red-600">{error}</p>}

            {result && (
                <div className="grid gap-2">
                    {result.explanation && (
                        <p className="text-[11px] text-gray-500">
                            <span className="text-gray-400">Filtres appliqués : </span>{result.explanation}
                        </p>
                    )}
                    {result.zoneApplied && (
                        <p className="text-[11px] text-gray-500 flex items-start gap-1.5">
                            <MapPin className="w-3 h-3 shrink-0 mt-0.5" />
                            La description ne nommait pas de zone : celle du profil est appliquée ({result.zoneApplied}).
                        </p>
                    )}
                    {result.relaxed.length > 0 && (
                        <p className="text-[11px] text-sky-700 flex items-start gap-1.5">
                            <Info className="w-3 h-3 shrink-0 mt-0.5" />
                            Trop peu de correspondances strictes : critères assouplis pour proposer au moins 10 établissements
                            ({result.relaxed.join(" puis ")}). Les lignes concernées sont marquées « élargi ».
                        </p>
                    )}
                    {(result.unsupported.length > 0 || result.ignored.length > 0) && (
                        <p className="text-[11px] text-amber-700 flex items-start gap-1.5">
                            <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
                            Non pris en compte : {[...result.unsupported, ...result.ignored].join(" · ")}
                        </p>
                    )}

                    {result.establishments.length === 0 ? (
                        <p className="text-[11px] text-gray-400">
                            Aucun établissement disponible pour cette typologie — élargissez la description, ou la zone.
                        </p>
                    ) : (
                        <div className="border border-gray-200 rounded-sm divide-y divide-gray-100 max-h-96 overflow-y-auto">
                            {result.establishments.map((est) => {
                                const added = alreadyAdded.has(est._id);
                                const addr = [est.address?.postcode, est.address?.city].filter(Boolean).join(" ");
                                return (
                                    <div key={est._id} className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <a
                                                    href={establishmentPage(est._id)} target="_blank" rel="noopener noreferrer"
                                                    title="Ouvrir la fiche dans un nouvel onglet"
                                                    className="text-sm font-medium text-gray-900 truncate hover:underline"
                                                >
                                                    {est.name}
                                                </a>
                                                {(est.googleRating !== null && est.googleRating !== undefined) && (
                                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-50 text-amber-700 text-[10px] rounded">
                                                        <Star className="w-2.5 h-2.5 fill-amber-500 text-amber-500" />
                                                        {est.googleRating.toFixed(1).replace(".", ",")}
                                                    </span>
                                                )}
                                                {est.relaxed && (
                                                    <span
                                                        title={`Trouvé une fois abandonnés : ${est.relaxed}`}
                                                        className="px-1.5 py-0.5 bg-sky-50 text-sky-700 text-[10px] rounded"
                                                    >
                                                        élargi
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-[11px] text-gray-400 truncate">
                                                {[est.type, addr, est.siret].filter(Boolean).join(" · ")}
                                            </div>
                                            {est.owner && (
                                                <div className="text-[11px] text-gray-500 truncate">
                                                    {[
                                                        est.owner.name,
                                                        est.owner.age !== null ? `${est.owner.age} ans` : null,
                                                        `${est.owner.establishmentCount} établissement${est.owner.establishmentCount > 1 ? "s" : ""}`,
                                                    ].filter(Boolean).join(" · ")}
                                                </div>
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => { if (!added) void onAdd(est); }}
                                            disabled={added}
                                            title={added ? "Déjà dans le mail" : "Ajouter au mail"}
                                            className={`shrink-0 w-6 h-6 flex items-center justify-center rounded border transition-colors ${
                                                added
                                                    ? "border-green-200 bg-green-50 text-green-600 cursor-default"
                                                    : "border-gray-200 text-gray-400 hover:border-black hover:text-black"
                                            }`}
                                        >
                                            {added ? <Check className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ─── Draft editor ─────────────────────────────────────────────────────────────

function CardEditor({ card, index, feedbackToken, onChange, onRemove }: {
    card: WeeklyMailDraftCard;
    index: number;
    feedbackToken?: string;
    onChange: (patch: Partial<WeeklyMailDraftCard>) => void;
    onRemove: () => void;
}) {
    const [open, setOpen] = useState(false);
    const ownerName = cardOwnerName(card);
    const field = (label: string, value: string | null, key: keyof WeeklyMailDraftCard, textarea = false) => (
        <div className={textarea ? "col-span-2" : ""}>
            <label className="text-[11px] text-gray-400 mb-1 block">{label}</label>
            {textarea ? (
                <textarea
                    value={value ?? ""}
                    onChange={(e) => onChange({ [key]: e.target.value } as any)}
                    rows={2}
                    className="w-full border border-gray-200 rounded-sm px-2 py-1.5 text-xs outline-none focus:border-gray-400 resize-y"
                />
            ) : (
                <input
                    value={value ?? ""}
                    onChange={(e) => onChange({ [key]: e.target.value || null } as any)}
                    className="w-full border border-gray-200 rounded-sm px-2 py-1.5 text-xs outline-none focus:border-gray-400"
                />
            )}
        </div>
    );

    return (
        <div className="border border-gray-200 rounded-sm bg-white">
            <div className="flex items-start gap-3 p-3">
                <span className="text-xs text-gray-300 font-mono pt-0.5">{index + 1}</span>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-gray-900">{card.name || "Sans nom"}</span>
                        <span className={`px-1.5 py-0.5 text-[10px] rounded ${SLOT_STYLES[card.slot] ?? SLOT_STYLES.signal}`}>
                            {SLOT_LABELS[card.slot] ?? card.slot}
                        </span>
                    </div>
                    <p className="text-[11px] text-gray-500 mt-0.5">{card.subtitle}</p>
                    <p className="text-[11px] text-gray-600 mt-1">
                        {card.reasonTag && <span className="text-emerald-700 font-medium">{card.reasonTag} · </span>}
                        {card.reason}
                    </p>
                    {/* New tabs: the draft being edited must not be navigated away from. */}
                    <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                        {card.establishmentId && (
                            <a
                                href={establishmentPage(card.establishmentId)}
                                target="_blank" rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:underline"
                            >
                                <Building2 className="w-3 h-3" /> Fiche établissement
                            </a>
                        )}
                        {ownerName && (
                            <a
                                href={ownerPage(ownerName)}
                                target="_blank" rel="noopener noreferrer"
                                title={`Tous les établissements de ${ownerName}`}
                                className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:underline"
                            >
                                <Users className="w-3 h-3" /> Page du dirigeant
                            </a>
                        )}
                        {feedbackToken && (
                            <a
                                href={feedbackPageUrl(feedbackToken)}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Ouvre la page d'avis telle que la voit le destinataire, sur l'API que ce back-office interroge"
                                className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:underline"
                            >
                                <MessageSquare className="w-3 h-3" /> Tester la page d'avis
                            </a>
                        )}
                    </div>
                </div>
                <button onClick={() => setOpen((v) => !v)} className="shrink-0 p-1 text-gray-400 hover:text-black" title="Modifier">
                    {open ? <ChevronUp className="w-4 h-4" /> : <PencilLine className="w-4 h-4" />}
                </button>
                <button onClick={onRemove} className="shrink-0 p-1 text-gray-300 hover:text-red-500" title="Retirer du mail">
                    <X className="w-4 h-4" />
                </button>
            </div>
            {open && (
                <div className="px-3 pb-3 border-t border-gray-100 pt-3 grid grid-cols-2 gap-3">
                    {field("Nom affiché", card.name, "name")}
                    {field("Sous-titre (type · adresse)", card.subtitle, "subtitle")}
                    {field("Dirigeant", card.owner, "owner")}
                    {field("Nom du dirigeant (lien dans le mail)", card.ownerName ?? ownerName, "ownerName")}
                    {field("Note & avis", card.rating, "rating")}
                    {field("Fiche Google (URL)", card.googleUrl, "googleUrl")}
                    {field("Téléphone", card.phone, "phone")}
                    {field("Email", card.email, "email")}
                    {field("Adresse (itinéraire)", card.address, "address")}
                    {field("Accroche (texte vert)", card.reasonTag, "reasonTag")}
                    {field("Pourquoi maintenant", card.reason, "reason", true)}
                </div>
            )}
        </div>
    );
}

// ─── Press block ──────────────────────────────────────────────────────────────

/**
 * The news of the profile's zone, newest first — to hand-pick the "Presse & actualités" block.
 *
 * Folded until asked for: most sends keep the three items picked automatically, and a list of a
 * few hundred headlines open by default would bury the rest of the editor. Not bound to the
 * send's period, on purpose — someone picking by hand may well want last month's opening.
 */
function ZoneNewsPicker({ draftId, press, onAdd }: {
    draftId: string;
    press: WeeklyPressItem[];
    onAdd: (item: WeeklyPressItem) => void;
}) {
    const PAGE = 30;
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState<WeeklyZoneNews[]>([]);
    const [total, setTotal] = useState(0);
    const [zone, setZone] = useState("");
    const [all, setAll] = useState(false);
    const [query, setQuery] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async (offset: number, reset: boolean) => {
        setLoading(true);
        setError(null);
        try {
            const data = await getDraftNews(draftId, {
                offset,
                limit: PAGE,
                status: all ? "all" : "approved",
                q: query.trim() || undefined,
            });
            setZone(data.zone);
            setTotal(data.total);
            setItems((prev) => (reset ? data.items : [...prev, ...data.items]));
        } catch (err: any) {
            setError(err?.response?.data?.error ?? "Actualités indisponibles.");
        } finally {
            setLoading(false);
        }
    }, [draftId, all, query]);

    // Reloaded from the top whenever the list is opened or its filters change; the search box is
    // debounced so a word being typed does not fire one request per letter.
    useEffect(() => {
        if (!open) return;
        const timer = setTimeout(() => { void load(0, true); }, 300);
        return () => clearTimeout(timer);
    }, [open, load]);

    const usedUrls = new Set(press.map((p) => p.url).filter(Boolean));
    const usedTexts = new Set(press.map((p) => p.text));

    return (
        <div className="border border-gray-200 rounded-sm">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 transition-colors"
            >
                {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                Toutes les actualités de la zone{zone ? ` (${zone})` : ""}
                {open && total > 0 && <span className="text-gray-400">· {total}</span>}
            </button>

            {open && (
                <div className="border-t border-gray-100 p-3 grid gap-2">
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="flex items-center gap-2 flex-1 min-w-[200px] border border-gray-200 rounded-sm px-2 py-1.5 focus-within:border-gray-400">
                            <Search className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                            <input
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Filtrer par titre…"
                                className="flex-1 text-xs outline-none bg-transparent placeholder-gray-400"
                            />
                        </div>
                        <label className="flex items-center gap-1.5 cursor-pointer text-[11px] text-gray-600">
                            <input
                                type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)}
                                className="w-3.5 h-3.5 accent-black"
                            />
                            Inclure les actualités pas encore validées
                        </label>
                    </div>

                    {error && <p className="text-[11px] text-red-600">{error}</p>}

                    {items.length === 0 && !loading ? (
                        <p className="text-[11px] text-gray-400 py-2">Aucune actualité pour cette zone.</p>
                    ) : (
                        <div className="divide-y divide-gray-100 max-h-80 overflow-y-auto border border-gray-100 rounded-sm">
                            {items.map((n) => {
                                const added = (n.link && usedUrls.has(n.link)) || usedTexts.has(n.press.text);
                                return (
                                    <div key={n._id} className="flex items-start gap-3 px-3 py-2">
                                        <span className="text-[10px] text-gray-400 w-16 shrink-0 pt-0.5 tabular-nums">
                                            {n.published ? fmtDate(n.published) : "—"}
                                        </span>
                                        <div className="flex-1 min-w-0">
                                            {n.link ? (
                                                <a
                                                    href={n.link} target="_blank" rel="noopener noreferrer"
                                                    className="text-xs text-gray-900 hover:underline"
                                                >
                                                    {n.title}
                                                </a>
                                            ) : (
                                                <span className="text-xs text-gray-900">{n.title}</span>
                                            )}
                                            <div className="text-[10px] text-gray-400 flex items-center gap-1.5 flex-wrap">
                                                {[n.source, n.city].filter(Boolean).join(" · ")}
                                                {n.status !== "approved" && (
                                                    <span className="px-1 py-px bg-amber-50 text-amber-700 rounded">non validée</span>
                                                )}
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => { if (!added) onAdd(n.press); }}
                                            disabled={Boolean(added)}
                                            title={added ? "Déjà dans le mail" : "Ajouter au mail"}
                                            className={`shrink-0 w-6 h-6 flex items-center justify-center rounded border transition-colors ${
                                                added
                                                    ? "border-green-200 bg-green-50 text-green-600 cursor-default"
                                                    : "border-gray-200 text-gray-400 hover:border-black hover:text-black"
                                            }`}
                                        >
                                            {added ? <Check className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <div className="flex items-center gap-3">
                        {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />}
                        {!loading && items.length < total && (
                            <button
                                type="button" onClick={() => load(items.length, false)}
                                className="px-2.5 py-1 border border-gray-200 rounded-sm text-[11px] text-gray-600 hover:bg-gray-50 transition-colors"
                            >
                                Plus anciennes ({total - items.length})
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

/** The press block of one mail: what it carries, editable line by line, in the order printed. */
function PressEditor({ press, onChange, disabled }: {
    press: WeeklyPressItem[];
    onChange: (press: WeeklyPressItem[]) => void;
    disabled?: boolean;
}) {
    const patch = (i: number, p: Partial<WeeklyPressItem>) => onChange(press.map((x, j) => (j === i ? { ...x, ...p } : x)));
    const move = (i: number, delta: number) => {
        const j = i + delta;
        if (j < 0 || j >= press.length) return;
        const next = [...press];
        [next[i], next[j]] = [next[j], next[i]];
        onChange(next);
    };

    return (
        <div className="grid gap-2">
            {press.length === 0 && (
                <p className="text-[11px] text-gray-400">
                    Aucune actualité dans ce mail : le bloc « Presse & actualités » n'y figurera pas.
                </p>
            )}
            {press.map((item, i) => (
                <div key={i} className="border border-gray-200 rounded-sm p-2.5 grid gap-1.5">
                    <div className="flex items-center gap-2">
                        <input
                            value={item.text} disabled={disabled}
                            onChange={(e) => patch(i, { text: e.target.value })}
                            placeholder="Titre affiché"
                            className="flex-1 border border-gray-200 rounded-sm px-2 py-1.5 text-xs outline-none focus:border-gray-400 disabled:bg-gray-50"
                        />
                        {!disabled && (
                            <>
                                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} title="Monter"
                                    className="p-1 text-gray-400 hover:text-black disabled:opacity-30">
                                    <ArrowUp className="w-3.5 h-3.5" />
                                </button>
                                <button type="button" onClick={() => move(i, 1)} disabled={i === press.length - 1} title="Descendre"
                                    className="p-1 text-gray-400 hover:text-black disabled:opacity-30">
                                    <ArrowDown className="w-3.5 h-3.5" />
                                </button>
                                <button type="button" onClick={() => onChange(press.filter((_, j) => j !== i))} title="Retirer du mail"
                                    className="p-1 text-gray-300 hover:text-red-500">
                                    <X className="w-4 h-4" />
                                </button>
                            </>
                        )}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <input
                            value={item.meta} disabled={disabled}
                            onChange={(e) => patch(i, { meta: e.target.value })}
                            placeholder="Source · ville · date"
                            className="border border-gray-200 rounded-sm px-2 py-1.5 text-[11px] outline-none focus:border-gray-400 disabled:bg-gray-50"
                        />
                        <input
                            value={item.url ?? ""} disabled={disabled}
                            onChange={(e) => patch(i, { url: e.target.value || null })}
                            placeholder="Lien de l'article (https://…)"
                            className="border border-gray-200 rounded-sm px-2 py-1.5 text-[11px] outline-none focus:border-gray-400 disabled:bg-gray-50"
                        />
                    </div>
                </div>
            ))}
            {!disabled && (
                <button
                    type="button"
                    onClick={() => onChange([...press, { text: "", meta: "", url: null }])}
                    className="justify-self-start flex items-center gap-1.5 px-2.5 py-1.5 border border-gray-200 rounded-sm text-xs text-gray-600 hover:bg-gray-50 transition-colors"
                >
                    <Plus className="w-3.5 h-3.5" /> Ajouter une actualité à la main
                </button>
            )}
        </div>
    );
}

function DraftEditor({ draftId, onBack }: { draftId: string; onBack: () => void }) {
    const [draft, setDraft] = useState<WeeklyMailDraft | null>(null);
    const [cards, setCards] = useState<WeeklyMailDraftCard[]>([]);
    const [subject, setSubject] = useState("");
    const [preheader, setPreheader] = useState("");
    const [customTitle, setCustomTitle] = useState("");
    const [recipients, setRecipients] = useState<WeeklyMailRecipient[]>([]);
    const [press, setPress] = useState<WeeklyPressItem[]>([]);
    const [feedback, setFeedback] = useState<WeeklyMailFeedbackItem[]>([]);

    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [testEmail, setTestEmail] = useState("");

    const load = useCallback(async (d: WeeklyMailDraft) => {
        setDraft(d);
        setCards(d.cards);
        setSubject(d.subject);
        setPreheader(d.preheader);
        setCustomTitle(d.customTitle);
        setRecipients(d.recipients);
        setPress(d.press ?? []);
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const d = await getDraft(draftId);
                if (!cancelled) await load(d);
                const fb = await listFeedback({ draftId });
                if (!cancelled) setFeedback(fb);
            } catch (err: any) {
                if (!cancelled) setError(err?.response?.data?.error ?? "Aperçu introuvable.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [draftId, load]);

    const save = async () => {
        setBusy("save");
        setError(null);
        try {
            // A line left without a title would print as an empty row in the mail.
            const cleanPress = press.filter((p) => p.text.trim());
            const d = await updateDraft(draftId, { subject, preheader, customTitle, cards, recipients, press: cleanPress });
            await load(d);
            setNotice("Modifications enregistrées.");
            setTimeout(() => setNotice(null), 2500);
        } catch (err: any) {
            setError(err?.response?.data?.error ?? "Enregistrement impossible.");
        } finally {
            setBusy(null);
        }
    };

    // This draft, by id — not "whichever one the profile sends next", which with several sends a
    // week can be another one. The establishments already in it are set aside so the new list
    // actually differs; they only come back if the zone cannot fill the mail without them.
    const regenerate = async () => {
        if (!draft) return;
        if (!window.confirm(
            "Régénérer remplace les établissements (hors épinglés) par de nouveaux et écrase vos modifications. Continuer ?"
        )) return;
        setBusy("regen");
        setError(null);
        try {
            const before = new Set(cards.map((c) => c.establishmentId));
            const d = await regenerateDraft(draftId);
            await load(d);
            const fresh = d.cards.filter((c) => !before.has(c.establishmentId)).length;
            setNotice(`Contenu régénéré : ${fresh} ${fresh > 1 ? "nouveaux établissements" : "nouvel établissement"} sur ${d.cards.length}.`);
            setTimeout(() => setNotice(null), 4000);
        } catch (err: any) {
            setError(err?.response?.data?.error ?? "Régénération impossible.");
        } finally {
            setBusy(null);
        }
    };

    const addAuto = async () => {
        setBusy("auto");
        setError(null);
        try {
            const d = await addAutoCard(draftId);
            await load(d);
            const added = d.cards[d.cards.length - 1];
            setNotice(`Ajouté : ${added?.name ?? "un établissement"} (${SLOT_LABELS[added?.slot ?? ""] ?? added?.slot}).`);
            setTimeout(() => setNotice(null), 3000);
        } catch (err: any) {
            setError(err?.response?.data?.error ?? "Ajout impossible.");
        } finally {
            setBusy(null);
        }
    };

    const addEstablishment = async (est: WeeklyEstablishment) => {
        setBusy("add");
        try {
            const d = await addDraftCard(draftId, est._id);
            await load(d);
            setError(null);
        } catch (err: any) {
            // The six-month block answers 409. It is a rule, not a failure, so it is put to
            // the admin as a choice — with the dates — rather than shown as an error.
            if (err?.response?.status === 409 && err?.response?.data?.code === "RESEND_BLOCKED") {
                const { lastSentAt, resendBlockedUntil, reason } = err.response.data;
                const what =
                    reason === "recipient" ? `a déjà été envoyé à ces destinataires le ${fmtDate(lastSentAt)}`
                        : reason === "cross_client" ? `a été envoyé à un autre client le ${fmtDate(lastSentAt)}`
                        : `est déjà dans un autre briefing en préparation`;
                const when = resendBlockedUntil
                    ? `Il ne redevient disponible que le ${fmtDate(resendBlockedUntil)}.\n\n`
                    : `Il partira dans cet autre envoi.\n\n`;
                const confirmed = window.confirm(
                    `« ${est.name} » ${what}.\n` + when + `L'ajouter quand même à cet envoi ?`
                );
                if (confirmed) {
                    const d = await addDraftCard(draftId, est._id, true);
                    await load(d);
                    setError(null);
                }
            } else {
                setError(err?.response?.data?.error ?? "Ajout impossible.");
            }
        } finally {
            setBusy(null);
        }
    };

    const sendTest = async () => {
        if (!testEmail.includes("@")) { setError("Adresse de test invalide."); return; }
        setBusy("test");
        setError(null);
        try {
            const { sent, failed } = await sendDraftTest(draftId, testEmail);
            setNotice(sent > 0 ? `Test envoyé à ${testEmail}.` : `Échec de l'envoi (${failed}).`);
            setTimeout(() => setNotice(null), 4000);
        } catch (err: any) {
            setError(err?.response?.data?.error ?? "Envoi de test impossible.");
        } finally {
            setBusy(null);
        }
    };

    const sendNow = async () => {
        if (!window.confirm(`Envoyer maintenant à ${recipients.length} destinataire(s) ?`)) return;
        setBusy("send");
        setError(null);
        try {
            const { sent, failed, draft: updated } = await sendDraftNow(draftId);
            await load(updated);
            setNotice(`Envoyé à ${sent} destinataire(s)${failed > 0 ? `, ${failed} échec(s)` : ""}.`);
        } catch (err: any) {
            setError(err?.response?.data?.error ?? "Envoi impossible.");
        } finally {
            setBusy(null);
        }
    };

    const cancel = async () => {
        if (!window.confirm("Annuler cet envoi ? Le mail ne partira pas au créneau prévu.")) return;
        setBusy("cancel");
        try {
            await cancelDraft(draftId);
            onBack();
        } catch (err: any) {
            setError(err?.response?.data?.error ?? "Annulation impossible.");
        } finally {
            setBusy(null);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center gap-2 text-sm text-gray-400 p-6">
                <Loader2 className="w-4 h-4 animate-spin" /> Chargement de l'aperçu…
            </div>
        );
    }
    if (!draft) {
        return (
            <div>
                <button onClick={onBack} className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-black mb-4">
                    <ArrowLeft className="w-3.5 h-3.5" /> Tous les profils
                </button>
                <div className="p-3 bg-red-50 border border-red-200 rounded-sm text-sm text-red-700">{error}</div>
            </div>
        );
    }

    const sent = draft.status === "sent";
    const daysLeft = daysUntil(draft.scheduledFor);

    return (
        <div>
            <button onClick={onBack} className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-black mb-4">
                <ArrowLeft className="w-3.5 h-3.5" /> Tous les profils
            </button>

            <div className="flex items-start justify-between mb-5 gap-4 flex-wrap">
                <div>
                    <h1 className="text-xl font-semibold text-gray-900">Aperçu de l'envoi</h1>
                    <div className="flex items-center gap-3 mt-1 text-sm text-gray-500 flex-wrap">
                        <span className="inline-flex items-center gap-1">
                            <Send className="w-3.5 h-3.5" />
                            {sent
                                ? `Envoyé le ${fmtDateTime(draft.sentAt ?? draft.scheduledFor)}`
                                : `Départ ${fmtDateTime(draft.scheduledFor)}${daysLeft >= 0 ? ` (J-${daysLeft})` : ""}`}
                        </span>
                        <span className={`px-1.5 py-0.5 text-[10px] rounded ${
                            sent ? "bg-emerald-50 text-emerald-700"
                                : draft.status === "cancelled" ? "bg-gray-100 text-gray-500"
                                : draft.status === "failed" ? "bg-red-50 text-red-700"
                                : "bg-amber-50 text-amber-700"
                        }`}>
                            {sent ? "Envoyé" : draft.status === "cancelled" ? "Annulé" : draft.status === "failed" ? "Échec" : draft.status === "sending" ? "Envoi en cours" : "En attente"}
                        </span>
                        {draft.edited && !sent && <span className="text-xs text-violet-600">modifié à la main</span>}
                    </div>
                </div>
                {!sent && (
                    <div className="flex items-center gap-2 flex-wrap">
                        <button
                            onClick={save} disabled={busy !== null}
                            className="flex items-center gap-1.5 px-3 py-2 bg-black text-white rounded-sm text-xs font-medium hover:bg-gray-800 disabled:opacity-50 transition-colors"
                        >
                            {busy === "save" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                            Enregistrer
                        </button>
                        <button
                            onClick={regenerate} disabled={busy !== null}
                            className="flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-sm text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
                        >
                            {busy === "regen" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                            Régénérer
                        </button>
                        <button
                            onClick={sendNow} disabled={busy !== null || recipients.length === 0}
                            className="flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-sm text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
                        >
                            {busy === "send" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                            Envoyer maintenant
                        </button>
                        <button
                            onClick={cancel} disabled={busy !== null}
                            className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 rounded-sm text-xs text-gray-500 hover:text-red-600 hover:border-red-200 disabled:opacity-50 transition-colors"
                        >
                            <Trash2 className="w-3.5 h-3.5" /> Annuler l'envoi
                        </button>
                    </div>
                )}
            </div>

            {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-sm text-sm text-red-700">{error}</div>}
            {notice && <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-sm text-sm text-emerald-700">{notice}</div>}
            {draft.error && <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-sm text-sm text-amber-800 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> {draft.error}
            </div>}

            <div className="grid gap-4">
                {/* Envelope */}
                <Section icon={Mail} title="En-tête du mail">
                    <div className="grid gap-3">
                        <div>
                            <label className="text-xs text-gray-500 mb-1 block">Objet</label>
                            <input
                                value={subject} onChange={(e) => setSubject(e.target.value)} disabled={sent}
                                className="w-full border border-gray-200 rounded-sm px-3 py-2 text-sm outline-none focus:border-gray-400 disabled:bg-gray-50"
                            />
                        </div>
                        <div>
                            <label className="text-xs text-gray-500 mb-1 block">Texte de prévisualisation (inbox)</label>
                            <input
                                value={preheader} onChange={(e) => setPreheader(e.target.value)} disabled={sent}
                                className="w-full border border-gray-200 rounded-sm px-3 py-2 text-sm outline-none focus:border-gray-400 disabled:bg-gray-50"
                            />
                        </div>
                        <div>
                            <label className="text-xs text-gray-500 mb-1 block">Titre du briefing (objet du mail)</label>
                            <input
                                value={customTitle} onChange={(e) => setCustomTitle(e.target.value)} disabled={sent}
                                className="w-full border border-gray-200 rounded-sm px-3 py-2 text-sm outline-none focus:border-gray-400 disabled:bg-gray-50"
                            />
                        </div>
                        <div>
                            <label className="text-xs text-gray-500 mb-1 block">Destinataires de cet envoi</label>
                            {sent ? (
                                <p className="text-xs text-gray-600">{recipients.map((r) => r.email).join(", ")}</p>
                            ) : (
                                <ChipInput
                                    values={recipients.map((r) => r.email)}
                                    onChange={(emails) => setRecipients(emails.map((email) => ({ email })))}
                                    placeholder="email@client.fr + Entrée"
                                />
                            )}
                        </div>
                    </div>
                </Section>

                {/* Cards */}
                <Section icon={Building2} title={`Établissements du mail (${cards.length})`}>
                    <div className="grid gap-2">
                        {cards.map((card, i) => (
                            <CardEditor
                                key={`${card.establishmentId}-${i}`}
                                card={card}
                                index={i}
                                feedbackToken={draft.feedbackTokens?.[card.establishmentId]}
                                onChange={(patch) => setCards((prev) => prev.map((c, j) => (j === i ? { ...c, ...patch } : c)))}
                                onRemove={() => setCards((prev) => prev.filter((_, j) => j !== i))}
                            />
                        ))}
                    </div>
                    {!sent && (
                        <div className="mt-3">
                            <div className="flex items-center gap-3 mb-3 flex-wrap">
                                <button
                                    type="button" onClick={addAuto} disabled={busy !== null}
                                    title="Choisi avec les critères du profil, dans le même ordre que les autres (signaux, puis slots d'enrichissement, puis la zone)"
                                    className="flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-sm text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
                                >
                                    {busy === "auto" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                                    Ajouter un établissement (mêmes critères)
                                </button>
                                <span className="text-[11px] text-gray-400">
                                    ou choisissez-le vous-même ci-dessous
                                </span>
                            </div>
                            <p className="text-xs text-gray-400 mb-2">Ajouter un établissement précis à cet envoi</p>
                            <EstablishmentSearch
                                onAdd={addEstablishment}
                                alreadyAdded={new Set(cards.map((c) => c.establishmentId))}
                                context={{ draftId }}
                            />
                            <p className="text-xs text-gray-400 mt-3 mb-1.5">Ou par SIRET / SIREN</p>
                            <SiretAdder
                                alreadyAdded={new Set(cards.map((c) => c.establishmentId))}
                                context={{ draftId }}
                                onResolved={async (ests) => {
                                    for (const est of ests) await addEstablishment(est);
                                }}
                            />
                        </div>
                    )}
                </Section>

                {/* Typology search */}
                {!sent && (
                    <Section icon={Tags} title="Chercher par typologie client">
                        <p className="text-xs text-gray-400 mb-3">
                            Partez d'un type de client enregistré depuis « Intelligence clients » : l'assistant cherche
                            dans la base les établissements qui lui ressemblent, en écartant ceux déjà présents dans ce
                            mail, ceux envoyés à ces destinataires depuis moins de 6 mois, et ceux partis chez un autre
                            client il y a moins de 3 semaines.
                        </p>
                        <TypologySearch
                            draftId={draftId}
                            alreadyAdded={new Set(cards.map((c) => c.establishmentId))}
                            onAdd={addEstablishment}
                        />
                    </Section>
                )}

                {/* Press */}
                <Section icon={Newspaper} title={`Presse & actualités (${press.length})`}>
                    <p className="text-xs text-gray-400 mb-3">
                        Uniquement des actualités de la zone du profil. Modifiez, réordonnez ou retirez chaque ligne,
                        puis enregistrez pour mettre à jour le rendu.
                    </p>
                    <PressEditor press={press} onChange={setPress} disabled={sent} />
                    {!sent && (
                        <div className="mt-3">
                            <ZoneNewsPicker draftId={draftId} press={press} onAdd={(item) => setPress((prev) => [...prev, item])} />
                        </div>
                    )}
                </Section>

                {/* Test send */}
                {!sent && (
                    <Section icon={Send} title="Envoi de test">
                        <div className="flex items-center gap-2 flex-wrap">
                            <input
                                value={testEmail} onChange={(e) => setTestEmail(e.target.value)}
                                placeholder="votre@email.fr"
                                className="flex-1 min-w-[220px] border border-gray-200 rounded-sm px-3 py-2 text-sm outline-none focus:border-gray-400"
                            />
                            <button
                                onClick={sendTest} disabled={busy !== null}
                                className="flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-sm text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
                            >
                                {busy === "test" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                                Envoyer un test
                            </button>
                            <span className="text-xs text-gray-400">Passe par Mailgun, exactement comme l'envoi réel (objet préfixé [TEST]).</span>
                        </div>
                    </Section>
                )}

                {/* Feedback */}
                {feedback.length > 0 && (
                    <Section icon={MessageSquare} title={`Avis des destinataires (${feedback.length})`}>
                        <div className="grid gap-2">
                            {feedback.map((f) => (
                                <div key={f._id} className="border border-gray-100 rounded-sm p-2.5">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className={`px-1.5 py-0.5 text-[10px] rounded ${f.vote === "relevant" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
                                            {f.vote === "relevant" ? "Correspond" : "Ne correspond pas"}
                                        </span>
                                        <span className="text-xs font-medium text-gray-800">{f.establishmentName ?? f.establishmentId}</span>
                                        {f.recipientEmail && <span className="text-[11px] text-gray-400">{f.recipientEmail}</span>}
                                        <span className="text-[11px] text-gray-400">{fmtDate(f.createdAt)}</span>
                                    </div>
                                    {f.comment && <p className="text-xs text-gray-600 mt-1">« {f.comment} »</p>}
                                </div>
                            ))}
                        </div>
                    </Section>
                )}
            </div>

            {/* Live preview */}
            <div className="mt-6">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Rendu du mail</span>
                    <span className="text-xs text-gray-400">
                        Les liens s'ouvrent dans un nouvel onglet · enregistrez pour rafraîchir après une modification.
                    </span>
                </div>
                <iframe
                    srcDoc={previewDoc(draft.html)}
                    title="Aperçu email hebdomadaire"
                    sandbox={PREVIEW_SANDBOX}
                    className="w-full border border-gray-200 rounded-sm"
                    style={{ height: "860px" }}
                />
            </div>
        </div>
    );
}

// ─── Profile detail ───────────────────────────────────────────────────────────

type Review = NonNullable<WeeklySentEstablishment["reviews"]>[number];

/**
 * One line of an establishment inside a brief, sent or to come. The name and the owner open
 * their page in the app, in a new tab — the same pages the mail links to.
 */
function SentEstablishmentRow({ establishmentId, name, siret, meta, slot, slotLabel, reason, reasonTag, owner, rating, vote, comment, reviews }: {
    establishmentId?: string | null;
    name: string;
    siret?: string | null;
    /** city for a past send, "type · adresse" for a card of the brief to come */
    meta?: string | null;
    slot: string;
    slotLabel: string;
    reason?: string | null;
    reasonTag?: string | null;
    owner?: string | null;
    rating?: string | null;
    vote?: "relevant" | "not_relevant" | null;
    comment?: string | null;
    /** every recipient's answer; falls back to `vote` / `comment` when absent */
    reviews?: Review[];
}) {
    const ownerName = owner ? cardOwnerName({ owner }) : null;
    const answers: Review[] = reviews?.length
        ? reviews
        : vote ? [{ vote, comment: comment ?? null, recipientEmail: null, at: null }] : [];

    return (
        <div className="px-3 py-2.5">
            <div className="flex items-start gap-2 flex-wrap">
                {establishmentId ? (
                    <a
                        href={establishmentPage(establishmentId)} target="_blank" rel="noopener noreferrer"
                        className="text-sm font-medium text-gray-900 hover:underline"
                    >
                        {name}
                    </a>
                ) : (
                    <span className="text-sm font-medium text-gray-900">{name}</span>
                )}
                <span className={`px-1.5 py-0.5 text-[10px] rounded ${SLOT_STYLES[slot] ?? SLOT_STYLES.signal}`}>
                    {slotLabel}
                </span>
            </div>
            <div className="text-[11px] text-gray-400 mt-0.5">
                {[meta, siret ? `SIRET ${siret}` : null, rating].filter(Boolean).join(" · ") || "—"}
                {owner && (
                    <>
                        {" · "}
                        {ownerName ? (
                            <a
                                href={ownerPage(ownerName)} target="_blank" rel="noopener noreferrer"
                                className="text-gray-500 hover:underline"
                            >
                                {owner}
                            </a>
                        ) : owner}
                    </>
                )}
            </div>
            {reason && (
                <p className="text-[11px] text-gray-600 mt-1">
                    {reasonTag && <span className="text-emerald-700 font-medium">{reasonTag} · </span>}
                    {reason}
                </p>
            )}
            {answers.length > 0 && (
                <div className="grid gap-1 mt-1.5">
                    {answers.map((r, i) => (
                        <div key={i} className="flex items-start gap-2 text-[11px]">
                            <span className={`px-1.5 py-0.5 text-[10px] rounded whitespace-nowrap ${
                                r.vote === "relevant" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"
                            }`}>
                                {r.vote === "relevant" ? "👍 Correspond" : "👎 Ne correspond pas"}
                            </span>
                            {r.recipientEmail && <span className="text-gray-400">{r.recipientEmail}</span>}
                            {r.comment && <span className="text-gray-600 italic">« {r.comment} »</span>}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ─── Calendar helpers ─────────────────────────────────────────────────────────

/** The slot colours as a bar rather than a badge — same families as `SLOT_STYLES`. */
const SLOT_BAR: Record<string, string> = {
    signal: "bg-gray-300",
    hygiene: "bg-amber-300",
    multi_owner: "bg-blue-300",
    rich_contacts: "bg-teal-300",
    manual: "bg-violet-300",
    backfill: "bg-pink-300",
};

/** "il y a 3 semaines" — the coarsest unit that still says something. */
function timeAgo(iso: string | Date): string {
    const days = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 86400000));
    if (days === 0) return "aujourd'hui";
    if (days === 1) return "hier";
    if (days < 7) return `il y a ${days} jours`;
    if (days < 60) {
        const weeks = Math.round(days / 7);
        return `il y a ${weeks} semaine${weeks > 1 ? "s" : ""}`;
    }
    return `il y a ${Math.round(days / 30)} mois`;
}

/** The day a send belongs to, in local time — the key the grid and the lookup share. */
function dayKey(d: Date): string {
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}
function startOfMonth(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), 1);
}
function addMonths(d: Date, n: number): Date {
    return new Date(d.getFullYear(), d.getMonth() + n, 1);
}
function sameMonth(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}
function monthLabel(d: Date): string {
    return d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
}
/** "2026-09-28T09:00", the value a `datetime-local` input reads and writes, in local time. */
function toLocalInput(d: Date): string {
    const p = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

const WEEKDAYS = ["L", "M", "M", "J", "V", "S", "D"];

/**
 * Every day the month's grid has to draw, Monday first — the days of the neighbouring months
 * that fill the first and last week included. The sixth week is dropped when the month does
 * not reach into it, so February never leaves an empty row.
 */
function monthGrid(month: Date): Date[] {
    const first = startOfMonth(month);
    const lead = (first.getDay() + 6) % 7;
    const days: Date[] = [];
    for (let i = 0; i < 42; i++) {
        days.push(new Date(first.getFullYear(), first.getMonth(), 1 - lead + i));
    }
    return sameMonth(days[35], month) ? days : days.slice(0, 35);
}

// The template renders a 640px container on a padded page; the thumbnail scales from that.
const MAIL_WIDTH = 700;

/**
 * The mail itself, shrunk into a calendar cell.
 *
 * An iframe rather than a generated image: the mail is already HTML, and scaling what will
 * actually be delivered keeps the thumbnail honest — there is no second rendering path to
 * drift from the first. Inert on purpose, so the cell around it owns the click.
 */
function MailThumb({ html, width, height, title, frame }: {
    html: string;
    width: number;
    height: number;
    title: string;
    /** border classes: the send's status is read off the frame */
    frame?: string;
}) {
    const scale = width / MAIL_WIDTH;
    return (
        <div
            className={`relative overflow-hidden rounded-xs bg-white ${frame ?? "border border-gray-200"}`}
            style={{ width, height }}
        >
            <iframe
                srcDoc={previewDoc(html)}
                title={title}
                sandbox={PREVIEW_SANDBOX}
                tabIndex={-1}
                scrolling="no"
                style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    border: 0,
                    width: MAIL_WIDTH,
                    height: height / scale,
                    transform: `scale(${scale})`,
                    transformOrigin: "top left",
                    pointerEvents: "none",
                }}
            />
        </div>
    );
}

const THUMB_W = 104;
const THUMB_H = 68;
/** When a day holds several sends, each gets a shorter frame so the cell stays readable. */
const THUMB_H_COMPACT = 40;

/** What a send was made of, as one bar: the slot mix is the shape of that brief. */
function SlotMix({ slots }: { slots: string[] }) {
    const counts = new Map<string, number>();
    for (const slot of slots) counts.set(slot, (counts.get(slot) ?? 0) + 1);
    const total = slots.length || 1;

    return (
        <span
            className="inline-flex h-1.5 w-12 shrink-0 rounded-full overflow-hidden bg-gray-100"
            title={[...counts].map(([slot, n]) => `${n} × ${SLOT_LABELS[slot] ?? slot}`).join(" · ")}
        >
            {[...counts].map(([slot, n]) => (
                <span key={slot} className={SLOT_BAR[slot] ?? SLOT_BAR.signal} style={{ width: `${(n / total) * 100}%` }} />
            ))}
        </span>
    );
}

// ─── Calendar entries ─────────────────────────────────────────────────────────

type CalEntryStatus = "sent" | WeeklyCalendarStatus;

/** One send on the calendar, whichever side of today it is on. */
interface CalEntry {
    key: string;
    at: Date;
    status: CalEntryStatus;
    kind: "series" | "oneoff";
    moved: boolean;
    originalAt: Date | null;
    note: string | null;
    draftId: string | null;
    recipients: string[];
    slots: string[];
    subject: string | null;
    error: string | null;
    /** a send that left — its establishments, with the votes */
    send?: WeeklyMailSendEntry;
    /** a send to come — as the calendar endpoint returned it (`item.at` is its identity) */
    item?: WeeklyCalendarItem;
}

function toEntries(cal: WeeklyMailCalendar): CalEntry[] {
    const sent: CalEntry[] = cal.sends.map((s) => ({
        key: `sent:${s.key}`,
        at: new Date(s.sentAt),
        status: "sent",
        kind: "series",
        moved: false,
        originalAt: null,
        note: null,
        draftId: s.draftId,
        recipients: s.recipients,
        slots: s.establishments.map((e) => e.slot),
        subject: null,
        error: null,
        send: s,
    }));
    const upcoming: CalEntry[] = cal.upcoming.map((it) => ({
        key: `up:${it.at}`,
        at: new Date(it.at),
        status: it.status,
        kind: it.kind,
        moved: it.moved,
        originalAt: it.originalAt ? new Date(it.originalAt) : null,
        note: it.note,
        draftId: it.draftId,
        recipients: it.recipients,
        slots: it.cards.map((c) => c.slot),
        subject: it.subject,
        error: it.error,
        item: it,
    }));
    return [...sent, ...upcoming].sort((a, b) => a.at.getTime() - b.at.getTime());
}

/** How each status reads: the frame of the thumbnail and the colour of its time. */
const STATUS_META: Record<CalEntryStatus, { label: string; frame: string; text: string }> = {
    sent: { label: "Envoyé", frame: "border border-gray-300", text: "text-gray-600" },
    prepared: { label: "Préparé", frame: "border border-dashed border-amber-400", text: "text-amber-700" },
    planned: { label: "Prévu", frame: "border border-dashed border-gray-300 bg-gray-50", text: "text-gray-500" },
    skipped: { label: "Sauté", frame: "border border-dotted border-gray-300 bg-gray-50", text: "text-gray-400 line-through" },
    cancelled: { label: "Annulé", frame: "border border-dotted border-gray-300 bg-gray-50", text: "text-gray-400 line-through" },
    failed: { label: "Échec", frame: "border border-red-300 bg-red-50", text: "text-red-700" },
};
/** A one-off is framed violet whatever its state before it leaves, so it reads as "hors série". */
const ONEOFF_FRAME = "border border-dashed border-violet-400 bg-violet-50/40";

function entryFrame(entry: CalEntry): string {
    if (entry.kind === "oneoff" && (entry.status === "prepared" || entry.status === "planned")) return ONEOFF_FRAME;
    return STATUS_META[entry.status].frame;
}

const isMovable = (e: CalEntry) => (e.status === "prepared" || e.status === "planned") && e.at.getTime() > Date.now();

function StatusChip({ entry }: { entry: CalEntry }) {
    const tone =
        entry.status === "sent" ? "bg-emerald-50 text-emerald-700"
            : entry.status === "prepared" ? "bg-amber-50 text-amber-700"
            : entry.status === "failed" ? "bg-red-50 text-red-700"
            : "bg-gray-100 text-gray-500";
    return (
        <span className="inline-flex items-center gap-1">
            <span className={`px-1.5 py-0.5 text-[10px] rounded ${tone}`}>{STATUS_META[entry.status].label}</span>
            {entry.kind === "oneoff" && <span className="px-1.5 py-0.5 text-[10px] rounded bg-violet-50 text-violet-700">Ponctuel</span>}
            {entry.moved && <span className="px-1.5 py-0.5 text-[10px] rounded bg-sky-50 text-sky-700">Déplacé</span>}
        </span>
    );
}

function voteCounts(entry: CalEntry): { up: number; down: number } {
    const ests = entry.send?.establishments ?? [];
    return {
        up: ests.filter((e) => (e.reviews?.length ? e.reviews.some((r) => r.vote === "relevant") : e.vote === "relevant")).length,
        down: ests.filter((e) => (e.reviews?.length ? e.reviews.some((r) => r.vote === "not_relevant") : e.vote === "not_relevant")).length,
    };
}

/** One send inside a day cell: its mail in miniature, its time, and the pop-up button. */
function CalendarEntryView({ entry, html, compact, dimmed, onOpen, onDetails, onDragStart, onDragEnd }: {
    entry: CalEntry;
    html: string | undefined;
    compact: boolean;
    dimmed: boolean;
    onOpen: () => void;
    onDetails: () => void;
    onDragStart: () => void;
    onDragEnd: () => void;
}) {
    const meta = STATUS_META[entry.status];
    const height = compact ? THUMB_H_COMPACT : THUMB_H;
    const movable = isMovable(entry);
    const { up, down } = voteCounts(entry);
    const PlaceholderIcon =
        entry.status === "planned" ? Clock
            : entry.status === "skipped" ? SkipForward
            : entry.status === "cancelled" ? Ban
            : entry.status === "failed" ? AlertTriangle
            : entry.draftId ? Loader2 : Mail;

    return (
        <div
            draggable={movable}
            onDragStart={(e) => {
                e.dataTransfer.setData("text/plain", entry.key);
                e.dataTransfer.effectAllowed = "move";
                onDragStart();
            }}
            onDragEnd={onDragEnd}
            className={`mt-1 ${movable ? "cursor-grab active:cursor-grabbing" : ""} ${dimmed ? "opacity-50" : ""}`}
        >
            <button
                type="button"
                onClick={onOpen}
                className="block text-left"
                title={[
                    `${meta.label} · ${fmtDateTime(entry.at.toISOString())}`,
                    entry.slots.length ? `${entry.slots.length} établissement(s)` : null,
                    entry.recipients.length ? entry.recipients.join(", ") : null,
                    movable ? "Glisser sur un autre jour pour déplacer cet envoi seulement" : null,
                ].filter(Boolean).join("\n")}
            >
                {html ? (
                    <MailThumb html={html} width={THUMB_W} height={height} title={`Mail du ${fmtDate(entry.at.toISOString())}`} frame={entryFrame(entry)} />
                ) : (
                    <div className={`flex items-center justify-center rounded-xs ${entryFrame(entry)}`} style={{ width: THUMB_W, height }}>
                        <PlaceholderIcon className={`w-3.5 h-3.5 text-gray-300 ${PlaceholderIcon === Loader2 ? "animate-spin" : ""}`} />
                    </div>
                )}
            </button>
            <div className="mt-0.5 flex items-center gap-1" style={{ width: THUMB_W }}>
                <span className={`text-[10px] tabular-nums ${meta.text}`}>{hhmm(entry.at.getHours(), entry.at.getMinutes())}</span>
                {entry.moved && <span title="Déplacé"><ArrowLeftRight className="w-2.5 h-2.5 text-sky-600" /></span>}
                {entry.kind === "oneoff" && <span title="Envoi ponctuel"><Zap className="w-2.5 h-2.5 text-violet-600" /></span>}
                {entry.slots.length > 0 && <SlotMix slots={entry.slots} />}
                {(up > 0 || down > 0) && (
                    <span className="text-[9px] text-gray-400 whitespace-nowrap">{up > 0 && "👍"}{down > 0 && "👎"}</span>
                )}
                <button
                    type="button"
                    onClick={onDetails}
                    title="Avis et réglages de cet envoi"
                    aria-label="Avis et réglages de cet envoi"
                    className="ml-auto p-0.5 rounded-sm text-gray-400 hover:text-black hover:bg-gray-100 transition-colors"
                >
                    <Info className="w-3 h-3" />
                </button>
            </div>
        </div>
    );
}

// ─── Mail viewer ──────────────────────────────────────────────────────────────

/**
 * One mail, big.
 *
 * The arrows walk the month from inside the viewer, so comparing two sends never means going
 * back to the grid; Escape closes, and the backdrop does too — a viewer that traps its reader
 * is one they escape by reloading the page.
 */
function MailModal({ entry, html, index, total, onStep, onClose, onOpenDraft, onDetails }: {
    entry: CalEntry;
    html: string | undefined;
    /** position in the month, so the viewer can say where it is and stop at the ends */
    index: number;
    total: number;
    onStep: (delta: number) => void;
    onClose: () => void;
    onOpenDraft: (id: string) => void;
    onDetails: () => void;
}) {
    const { up, down } = voteCounts(entry);

    useEffect(() => {
        // `KeyboardEvent` is React's type in this file, hence the global one spelled out.
        const onKey = (e: globalThis.KeyboardEvent) => {
            if (e.key === "Escape") onClose();
            if (e.key === "ArrowLeft") onStep(-1);
            if (e.key === "ArrowRight") onStep(1);
        };
        window.addEventListener("keydown", onKey);
        // The page behind must not scroll under the viewer.
        const previous = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = previous;
        };
    }, [onClose, onStep]);

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={onClose}
            role="presentation"
        >
            {/* A definite height, not just a cap: the mail below fills what is left of the
                dialog with `flex-1`, and a percentage height resolves against nothing when the
                parent is free to shrink to its content. */}
            <div
                className="flex flex-col w-full max-w-5xl h-[92vh] bg-white rounded-sm shadow-xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label={`Mail du ${fmtDateTime(entry.at.toISOString())}`}
            >
                <div className="flex items-center gap-2.5 px-3 py-2 border-b border-gray-200 flex-wrap">
                    <span className="text-xs font-medium text-gray-800">{fmtDateTime(entry.at.toISOString())}</span>
                    <StatusChip entry={entry} />
                    {entry.status === "sent" && <span className="text-[11px] text-gray-400">{timeAgo(entry.at)}</span>}
                    {entry.slots.length > 0 && <SlotMix slots={entry.slots} />}
                    {entry.slots.length > 0 && (
                        <span className="text-[11px] text-gray-500">
                            {entry.slots.length} établissement{entry.slots.length > 1 ? "s" : ""}
                        </span>
                    )}
                    {up > 0 && <span className="px-1.5 py-0.5 text-[10px] rounded bg-emerald-50 text-emerald-700">👍 {up}</span>}
                    {down > 0 && <span className="px-1.5 py-0.5 text-[10px] rounded bg-red-50 text-red-700">👎 {down}</span>}
                    <span className="text-[11px] text-gray-400 truncate max-w-[220px]">{entry.recipients.join(", ")}</span>

                    <div className="ml-auto flex items-center gap-1 shrink-0">
                        <button
                            onClick={() => onStep(-1)}
                            disabled={index === 0}
                            aria-label="Envoi précédent"
                            className="p-1 rounded-sm text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                        >
                            <ChevronLeft className="w-3.5 h-3.5" />
                        </button>
                        <span className="text-[11px] text-gray-400 tabular-nums">{index + 1}/{total}</span>
                        <button
                            onClick={() => onStep(1)}
                            disabled={index === total - 1}
                            aria-label="Envoi suivant"
                            className="p-1 rounded-sm text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                        >
                            <ChevronRight className="w-3.5 h-3.5" />
                        </button>
                        <button
                            onClick={onDetails}
                            className="ml-1 flex items-center gap-1 px-2 py-1 border border-gray-200 rounded-sm text-[11px] text-gray-600 hover:bg-gray-50 transition-colors"
                        >
                            <Info className="w-3 h-3" /> Avis & réglages
                        </button>
                        {entry.draftId && (
                            <button
                                onClick={() => onOpenDraft(entry.draftId!)}
                                className="flex items-center gap-1 px-2 py-1 border border-gray-200 rounded-sm text-[11px] text-gray-600 hover:bg-gray-50 transition-colors"
                            >
                                <PencilLine className="w-3 h-3" /> Éditeur
                            </button>
                        )}
                        <button
                            onClick={onClose}
                            aria-label="Fermer"
                            className="p-1 rounded-sm text-gray-500 hover:bg-gray-100 transition-colors"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                <div className="flex-1 min-h-0 flex flex-col bg-gray-50 p-3">
                    {!entry.draftId ? (
                        <p className="text-[11px] text-gray-400">
                            Envoi enregistré à la main : aucun brief n'a été conservé, il n'y a pas de rendu à afficher.
                        </p>
                    ) : html === undefined ? (
                        <div className="flex items-center gap-2 text-xs text-gray-400 py-6">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Chargement du mail…
                        </div>
                    ) : html === "" ? (
                        <p className="text-[11px] text-gray-400">Le brief de cet envoi n'est plus disponible.</p>
                    ) : (
                        <iframe
                            srcDoc={previewDoc(html)}
                            title={`Mail du ${fmtDateTime(entry.at.toISOString())}`}
                            sandbox={PREVIEW_SANDBOX}
                            className="flex-1 min-h-0 w-full border border-gray-200 rounded-sm bg-white"
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

// ─── Per-send pop-up ──────────────────────────────────────────────────────────

/** A small dialog over the calendar: Escape and the backdrop close it, like the viewer. */
function Dialog({ title, onClose, children, wide }: {
    title: React.ReactNode;
    onClose: () => void;
    children: React.ReactNode;
    wide?: boolean;
}) {
    useEffect(() => {
        const onKey = (e: globalThis.KeyboardEvent) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose} role="presentation">
            <div
                className={`w-full ${wide ? "max-w-2xl" : "max-w-md"} max-h-[88vh] flex flex-col bg-white rounded-sm shadow-xl overflow-hidden`}
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
            >
                <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-200">
                    <div className="flex-1 min-w-0">{title}</div>
                    <button onClick={onClose} aria-label="Fermer" className="p-1 rounded-sm text-gray-500 hover:bg-gray-100 transition-colors">
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <div className="flex-1 min-h-0 overflow-y-auto p-4">{children}</div>
            </div>
        </div>
    );
}

/**
 * Everything about ONE send, from the calendar: the reviews of a send that left, and for one to
 * come its own date and time, skip / restore, preparing it now, or opening its editor. What is
 * changed here applies to this send alone — the profile's weekly pattern does not move.
 */
function EntryDetails({ entry, profileId, onClose, onChanged, onOpenDraft, onView }: {
    entry: CalEntry;
    profileId: string;
    onClose: () => void;
    /** reloads the calendar; resolves once it is on screen */
    onChanged: () => Promise<void>;
    onOpenDraft: (id: string) => void;
    onView: () => void;
}) {
    const [when, setWhen] = useState(toLocalInput(entry.at));
    const [busy, setBusy] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const future = entry.at.getTime() > Date.now();
    const at = entry.item?.at;

    useEffect(() => { setWhen(toLocalInput(entry.at)); }, [entry.at]);

    const act = async (name: string, fn: () => Promise<unknown>, closeAfter = false) => {
        setBusy(name);
        setError(null);
        try {
            await fn();
            await onChanged();
            if (closeAfter) onClose();
        } catch (err: any) {
            setError(err?.response?.data?.error ?? "Action impossible.");
        } finally {
            setBusy(null);
        }
    };

    const move = (to: Date) =>
        act("move", () => updateOccurrence(profileId, { at: at!, action: "move", to: to.toISOString() }), true);

    const prepare = async () => {
        setBusy("prepare");
        setError(null);
        try {
            const draft = await prepareOccurrence(profileId, at!);
            onOpenDraft(draft._id);
        } catch (err: any) {
            setError(err?.response?.data?.error ?? "Préparation impossible.");
            setBusy(null);
        }
    };

    const btn = "flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-sm text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors";
    const cards = entry.item?.cards ?? [];
    const ests = entry.send?.establishments ?? [];
    const { up, down } = voteCounts(entry);

    return (
        <Dialog
            wide={entry.status === "sent" || cards.length > 0}
            onClose={onClose}
            title={
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-gray-900">{fmtDateTime(entry.at.toISOString())}</span>
                    <StatusChip entry={entry} />
                </div>
            }
        >
            <div className="grid gap-4">
                <div className="grid gap-1 text-[11px] text-gray-500">
                    {entry.subject && <p className="text-xs text-gray-800">{entry.subject}</p>}
                    {entry.moved && entry.originalAt && (
                        <p className="flex items-center gap-1.5">
                            <ArrowLeftRight className="w-3 h-3 text-sky-600" />
                            Déplacé : prévu à l'origine le {fmtDateTime(entry.originalAt.toISOString())}
                        </p>
                    )}
                    {entry.kind === "oneoff" && <p>Envoi ponctuel, hors rythme hebdomadaire (ne compte pas dans le nombre d'envois de la série).</p>}
                    {entry.note && <p className="italic">« {entry.note} »</p>}
                    {entry.recipients.length > 0 && (
                        <p className="flex items-start gap-1.5"><Users className="w-3 h-3 mt-0.5 shrink-0" />{entry.recipients.join(", ")}</p>
                    )}
                    {entry.error && (
                        <p className="flex items-center gap-1.5 text-red-700"><AlertTriangle className="w-3 h-3" />{entry.error}</p>
                    )}
                </div>

                {error && <div className="p-2 bg-red-50 border border-red-200 rounded-sm text-xs text-red-700">{error}</div>}

                {/* ── A send to come: when, and what to do with it ── */}
                {(entry.status === "prepared" || entry.status === "planned") && future && (
                    <div className="grid gap-2">
                        <label className="text-xs text-gray-500">Date et heure de cet envoi</label>
                        <div className="flex items-center gap-2 flex-wrap">
                            <input
                                type="datetime-local"
                                value={when}
                                min={toLocalInput(new Date())}
                                onChange={(e) => setWhen(e.target.value)}
                                className="border border-gray-200 rounded-sm px-2 py-1.5 text-sm outline-none focus:border-gray-400"
                            />
                            <button
                                type="button"
                                onClick={() => when && move(new Date(when))}
                                disabled={busy !== null || !when || new Date(when).getTime() === entry.at.getTime()}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-black text-white rounded-sm text-xs font-medium hover:bg-gray-800 disabled:opacity-50 transition-colors"
                            >
                                {busy === "move" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                                Déplacer
                            </button>
                            {entry.moved && entry.originalAt && entry.originalAt.getTime() > Date.now() && (
                                <button type="button" onClick={() => move(entry.originalAt!)} disabled={busy !== null} className={btn}>
                                    <RotateCcw className="w-3.5 h-3.5" /> Remettre sur son créneau
                                </button>
                            )}
                        </div>
                        <p className="text-[11px] text-gray-400">
                            Ne change que cet envoi : les suivants gardent le rythme du profil. Vous pouvez aussi le glisser sur un autre jour du calendrier.
                        </p>

                        <div className="flex items-center gap-2 flex-wrap mt-1">
                            {entry.status === "planned" ? (
                                <button type="button" onClick={prepare} disabled={busy !== null} className={btn}
                                    title="Construit le brief maintenant pour pouvoir le relire et le modifier">
                                    {busy === "prepare" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                                    Préparer maintenant
                                </button>
                            ) : (
                                <>
                                    <button type="button" onClick={() => onOpenDraft(entry.draftId!)} disabled={busy !== null} className={btn}>
                                        <PencilLine className="w-3.5 h-3.5" /> Ouvrir l'éditeur
                                    </button>
                                    <button type="button" onClick={onView} disabled={busy !== null} className={btn}>
                                        <Eye className="w-3.5 h-3.5" /> Voir le mail
                                    </button>
                                </>
                            )}
                            <button
                                type="button"
                                onClick={() => act("skip", () => updateOccurrence(profileId, { at: at!, action: "skip" }), entry.kind === "oneoff" && entry.status === "planned")}
                                disabled={busy !== null}
                                className={btn}
                                title={entry.status === "prepared" ? "Le brief préparé est conservé et peut être rétabli" : undefined}
                            >
                                {busy === "skip" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <SkipForward className="w-3.5 h-3.5" />}
                                {entry.kind === "oneoff" && entry.status === "planned" ? "Supprimer cet envoi" : "Sauter cet envoi"}
                            </button>
                            {entry.kind === "oneoff" && entry.status === "prepared" && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (window.confirm("Supprimer cet envoi ponctuel ? Son brief préparé sera annulé.")) {
                                            void act("delete", () => updateOccurrence(profileId, { at: at!, action: "delete" }), true);
                                        }
                                    }}
                                    disabled={busy !== null}
                                    className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-sm text-xs text-gray-500 hover:text-red-600 hover:border-red-200 disabled:opacity-50 transition-colors"
                                >
                                    <Trash2 className="w-3.5 h-3.5" /> Supprimer
                                </button>
                            )}
                        </div>
                        {entry.status === "planned" && (
                            <p className="text-[11px] text-gray-400">
                                Le brief sera préparé automatiquement {PREVIEW_LEAD_DAYS} jours avant son départ.
                            </p>
                        )}
                    </div>
                )}

                {/* An overdue brief the cron has not sent yet: it is leaving, nothing to plan. */}
                {entry.status === "prepared" && !future && (
                    <p className="text-xs text-amber-700">Départ imminent : ce brief part au prochain passage de l'envoi automatique.</p>
                )}

                {/* ── Skipped or cancelled ── */}
                {(entry.status === "skipped" || entry.status === "cancelled") && (
                    future ? (
                        <div className="flex items-center gap-2 flex-wrap">
                            <button
                                type="button"
                                onClick={() => act("restore", () => updateOccurrence(profileId, { at: at!, action: "restore" }))}
                                disabled={busy !== null}
                                className={btn}
                            >
                                {busy === "restore" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                                Rétablir cet envoi
                            </button>
                            {entry.draftId && (
                                <button type="button" onClick={onView} className={btn}><Eye className="w-3.5 h-3.5" /> Voir le brief</button>
                            )}
                        </div>
                    ) : (
                        <p className="text-xs text-gray-400">Cet envoi n'est pas parti.</p>
                    )
                )}

                {entry.status === "failed" && entry.draftId && (
                    <div className="flex items-center gap-2 flex-wrap">
                        <button type="button" onClick={() => onOpenDraft(entry.draftId!)} className={btn}>
                            <PencilLine className="w-3.5 h-3.5" /> Ouvrir l'éditeur
                        </button>
                    </div>
                )}

                {/* ── A send that left: how it was received ── */}
                {entry.status === "sent" && (
                    <div className="grid gap-2">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs font-medium text-gray-700">Avis des destinataires</span>
                            <span className="px-1.5 py-0.5 text-[10px] rounded bg-emerald-50 text-emerald-700">👍 {up}</span>
                            <span className="px-1.5 py-0.5 text-[10px] rounded bg-red-50 text-red-700">👎 {down}</span>
                            <span className="text-[11px] text-gray-400">{timeAgo(entry.at)}</span>
                            <div className="ml-auto flex items-center gap-2">
                                {entry.draftId && (
                                    <>
                                        <button type="button" onClick={onView} className={btn}><Eye className="w-3.5 h-3.5" /> Voir le mail</button>
                                        <button type="button" onClick={() => onOpenDraft(entry.draftId!)} className={btn}>
                                            <ExternalLink className="w-3.5 h-3.5" /> Éditeur
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                        {ests.every((e) => !e.vote && !e.reviews?.length) && (
                            <p className="text-[11px] text-gray-400">Aucun avis reçu pour cet envoi pour l'instant.</p>
                        )}
                        <div className="border border-gray-200 rounded-sm divide-y divide-gray-100">
                            {ests.map((est) => (
                                <SentEstablishmentRow
                                    key={`${entry.key}-${est.establishmentId}`}
                                    establishmentId={est.establishmentId}
                                    name={est.name}
                                    siret={est.siret}
                                    meta={est.city}
                                    slot={est.slot}
                                    slotLabel={est.slotLabel}
                                    reason={est.reason}
                                    reasonTag={est.reasonTag}
                                    owner={est.owner}
                                    rating={est.rating}
                                    vote={est.vote}
                                    comment={est.comment}
                                    reviews={est.reviews}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {/* ── What a prepared brief carries ── */}
                {entry.status !== "sent" && cards.length > 0 && (
                    <div className="grid gap-2">
                        <span className="text-xs font-medium text-gray-700">
                            {cards.length} établissement{cards.length > 1 ? "s" : ""} dans ce brief
                        </span>
                        <div className="border border-gray-200 rounded-sm divide-y divide-gray-100">
                            {cards.map((card) => (
                                <SentEstablishmentRow
                                    key={card.establishmentId}
                                    establishmentId={card.establishmentId}
                                    name={card.name}
                                    meta={card.subtitle}
                                    slot={card.slot}
                                    slotLabel={SLOT_LABELS[card.slot] ?? card.slotLabel ?? card.slot}
                                    reason={card.reason}
                                    reasonTag={card.reasonTag}
                                    owner={card.owner}
                                    rating={card.rating}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </Dialog>
    );
}

/** A send outside the pattern — "et celui-là, le jeudi 14h, en plus". */
function OneOffDialog({ day, defaultTime, profileId, onClose, onCreated }: {
    day: Date;
    defaultTime: string;
    profileId: string;
    onClose: () => void;
    onCreated: () => Promise<void>;
}) {
    const [date, setDate] = useState(toLocalInput(day).slice(0, 10));
    const [time, setTime] = useState(defaultTime);
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const submit = async () => {
        const at = new Date(`${date}T${time}`);
        if (isNaN(at.getTime())) { setError("Date invalide."); return; }
        setBusy(true);
        setError(null);
        try {
            await createOccurrence(profileId, at, note.trim() || undefined);
            await onCreated();
            onClose();
        } catch (err: any) {
            setError(err?.response?.data?.error ?? "Création impossible.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog onClose={onClose} title={<span className="text-sm font-medium text-gray-900">Nouvel envoi ponctuel</span>}>
            <div className="grid gap-3">
                <p className="text-[11px] text-gray-500">
                    Un envoi en plus, hors rythme hebdomadaire, avec les critères et les destinataires du profil.
                    Son brief est préparé {PREVIEW_LEAD_DAYS} jours avant, ou tout de suite depuis son pop-up.
                </p>
                <div className="flex items-center gap-2 flex-wrap">
                    <input
                        type="date" value={date} min={toLocalInput(new Date()).slice(0, 10)}
                        onChange={(e) => setDate(e.target.value)}
                        className="border border-gray-200 rounded-sm px-2 py-1.5 text-sm outline-none focus:border-gray-400"
                    />
                    <input
                        type="time" value={time} onChange={(e) => setTime(e.target.value)}
                        className="border border-gray-200 rounded-sm px-2 py-1.5 text-sm outline-none focus:border-gray-400"
                    />
                </div>
                <input
                    value={note} onChange={(e) => setNote(e.target.value)} maxLength={200}
                    placeholder="Note interne (facultatif) : « relance salon Sirha »…"
                    className="border border-gray-200 rounded-sm px-2 py-1.5 text-xs outline-none focus:border-gray-400"
                />
                {error && <div className="p-2 bg-red-50 border border-red-200 rounded-sm text-xs text-red-700">{error}</div>}
                <div className="flex items-center gap-2">
                    <button
                        type="button" onClick={submit} disabled={busy || !date || !time}
                        className="flex items-center gap-1.5 px-3 py-2 bg-black text-white rounded-sm text-xs font-medium hover:bg-gray-800 disabled:opacity-50 transition-colors"
                    >
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CalendarPlus className="w-3.5 h-3.5" />}
                        Programmer cet envoi
                    </button>
                    <button type="button" onClick={onClose} className="px-3 py-2 text-xs text-gray-500 hover:text-black">Annuler</button>
                </div>
            </div>
        </Dialog>
    );
}

/** What the colours and frames of the calendar mean. */
function CalendarLegend() {
    const frame = (cls: string) => <span className={`inline-block w-5 h-3.5 rounded-xs ${cls}`} />;
    return (
        <div className="mt-3 border border-gray-200 rounded-sm px-3 py-2.5 grid gap-2 text-[11px] text-gray-600">
            <div className="flex items-center gap-x-4 gap-y-1.5 flex-wrap">
                <span className="text-gray-400 w-24 shrink-0">Composition</span>
                {Object.entries(SLOT_LABELS).map(([slot, label]) => (
                    <span key={slot} className="inline-flex items-center gap-1.5">
                        <span className={`inline-block w-3 h-1.5 rounded-full ${SLOT_BAR[slot] ?? SLOT_BAR.signal}`} />
                        {label}
                    </span>
                ))}
            </div>
            <div className="flex items-center gap-x-4 gap-y-1.5 flex-wrap">
                <span className="text-gray-400 w-24 shrink-0">Statut</span>
                <span className="inline-flex items-center gap-1.5">{frame(STATUS_META.sent.frame)} Envoyé</span>
                <span className="inline-flex items-center gap-1.5">{frame(STATUS_META.prepared.frame)} Préparé (modifiable)</span>
                <span className="inline-flex items-center gap-1.5">{frame(STATUS_META.planned.frame)} Prévu, pas encore préparé</span>
                <span className="inline-flex items-center gap-1.5">{frame(ONEOFF_FRAME)} Envoi ponctuel</span>
                <span className="inline-flex items-center gap-1.5">{frame(STATUS_META.skipped.frame)} Sauté / annulé</span>
                <span className="inline-flex items-center gap-1.5">{frame(STATUS_META.failed.frame)} Échec</span>
            </div>
            <div className="flex items-center gap-x-4 gap-y-1.5 flex-wrap">
                <span className="text-gray-400 w-24 shrink-0">Repères</span>
                <span className="inline-flex items-center gap-1.5"><ArrowLeftRight className="w-3 h-3 text-sky-600" /> Déplacé</span>
                <span className="inline-flex items-center gap-1.5"><Zap className="w-3 h-3 text-violet-600" /> Ponctuel</span>
                <span>👍 / 👎 Avis reçus</span>
                <span className="inline-flex items-center gap-1.5"><Info className="w-3 h-3" /> Avis et réglages de l'envoi</span>
                <span className="inline-flex items-center gap-1.5"><Plus className="w-3 h-3" /> Ajouter un envoi ponctuel (survol d'un jour)</span>
            </div>
            <p className="text-gray-400">
                Glissez un envoi à venir sur un autre jour pour le déplacer (même heure) : seul cet envoi change, pas le rythme du profil.
            </p>
        </div>
    );
}

// ─── Calendar ─────────────────────────────────────────────────────────────────

/**
 * The profile's sends as a calendar: what left, what is prepared, and every send still to come
 * in the series — as far ahead as the grid is paged, since the future is computed server-side
 * from the pattern, the moves, the skips and the one-offs.
 *
 * The month is the unit, and that is what keeps the thumbnails affordable: a rendered mail only
 * exists on its draft, so each one is a request; bounding the page to a month bounds the
 * fetching. Nothing opens by itself — a click on a mail opens it big, the ⓘ button opens its
 * reviews and its own settings.
 */
function SendCalendar({ profile, onOpenDraft, onChanged }: {
    profile: WeeklyMailProfile;
    onOpenDraft: (id: string) => void;
    /** something about the profile changed (its next send, typically) */
    onChanged: () => void;
}) {
    const profileId = profile._id;
    const [month, setMonth] = useState(() => startOfMonth(new Date()));
    const [cal, setCal] = useState<WeeklyMailCalendar | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    // Fetched mails are kept across month changes: paging back must not re-fetch what has
    // already been rendered once.
    const [htmlById, setHtmlById] = useState<Record<string, string>>({});
    const [viewKey, setViewKey] = useState<string | null>(null);
    const [detailsKey, setDetailsKey] = useState<string | null>(null);
    const [createDay, setCreateDay] = useState<Date | null>(null);
    const [dragKey, setDragKey] = useState<string | null>(null);
    const [dropDay, setDropDay] = useState<string | null>(null);
    // Months can be paged faster than they load: only the latest request may land.
    const requestRef = useRef(0);

    const days = monthGrid(month);
    const rangeKey = `${days[0].toDateString()}|${days[days.length - 1].toDateString()}`;

    const reload = useCallback(async () => {
        const request = ++requestRef.current;
        setLoading(true);
        try {
            const from = new Date(days[0]);
            from.setHours(0, 0, 0, 0);
            const to = new Date(days[days.length - 1]);
            to.setHours(23, 59, 59, 999);
            const data = await getProfileCalendar(profileId, from, to);
            if (request !== requestRef.current) return;
            setCal(data);
            setError(null);
        } catch (err: any) {
            if (request === requestRef.current) setError(err?.response?.data?.error ?? "Calendrier indisponible.");
        } finally {
            if (request === requestRef.current) setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profileId, rangeKey]);

    useEffect(() => { void reload(); }, [reload]);

    const refresh = useCallback(async () => {
        await reload();
        onChanged();
    }, [reload, onChanged]);

    const entries = cal ? toEntries(cal) : [];
    const byDay = new Map<string, CalEntry[]>();
    for (const e of entries) byDay.set(dayKey(e.at), [...(byDay.get(dayKey(e.at)) ?? []), e]);

    // The mails of the grid, fetched when it opens. A draft that no longer exists must not blank
    // the whole month, so each failure is kept as "" rather than thrown.
    useEffect(() => {
        let cancelled = false;
        const missing = Array.from(new Set(
            entries.map((e) => e.draftId).filter((id): id is string => Boolean(id) && !(id! in htmlById))
        ));
        if (missing.length === 0) return;
        (async () => {
            const loaded = await Promise.all(
                missing.map(async (id) => {
                    try { return [id, (await getDraft(id)).html] as const; }
                    catch { return [id, ""] as const; }
                })
            );
            if (!cancelled) setHtmlById((prev) => ({ ...prev, ...Object.fromEntries(loaded) }));
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cal]);

    // The viewer walks the sends of the month that have a mail to show.
    const viewable = entries.filter((e) => sameMonth(e.at, month) && (e.draftId || e.status === "sent"));
    const viewIndex = viewable.findIndex((e) => e.key === viewKey);
    const step = useCallback((delta: number) => {
        setViewKey((key) => {
            const i = viewable.findIndex((e) => e.key === key);
            const next = viewable[i + delta];
            return next ? next.key : key;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewable.map((e) => e.key).join(",")]);
    const closeViewer = useCallback(() => setViewKey(null), []);
    const detailsEntry = detailsKey ? entries.find((e) => e.key === detailsKey) ?? null : null;

    const open = (entry: CalEntry) => {
        if (entry.draftId || entry.status === "sent") setViewKey(entry.key);
        else setDetailsKey(entry.key);
    };

    const drop = async (day: Date) => {
        const entry = entries.find((e) => e.key === dragKey);
        setDragKey(null);
        setDropDay(null);
        if (!entry?.item) return;
        const to = new Date(day);
        to.setHours(entry.at.getHours(), entry.at.getMinutes(), 0, 0);
        if (dayKey(to) === dayKey(entry.at)) return;
        if (to.getTime() <= Date.now()) {
            setError("Un envoi ne peut être déplacé que vers une date à venir.");
            return;
        }
        setLoading(true);
        try {
            await updateOccurrence(profileId, { at: entry.item.at, action: "move", to: to.toISOString() });
            setError(null);
            await refresh();
        } catch (err: any) {
            setError(err?.response?.data?.error ?? "Déplacement impossible.");
            setLoading(false);
        }
    };

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayKey = dayKey(new Date());
    const firstSlot = scheduleSlots(profile.schedule)[0];
    const inMonth = entries.filter((e) => sameMonth(e.at, month));
    const sentCount = inMonth.filter((e) => e.status === "sent").length;
    const comingCount = inMonth.filter((e) => e.status === "prepared" || e.status === "planned").length;

    return (
        <div>
            <div className="flex items-center gap-2 mb-2 flex-wrap">
                <button
                    onClick={() => setMonth(addMonths(month, -1))}
                    aria-label="Mois précédent"
                    className="p-1 border border-gray-200 rounded-sm text-gray-500 hover:bg-gray-50 transition-colors"
                >
                    <ChevronLeft className="w-3.5 h-3.5" />
                </button>
                <span className="text-sm font-medium text-gray-800 capitalize min-w-[130px] text-center">
                    {monthLabel(month)}
                </span>
                <button
                    onClick={() => setMonth(addMonths(month, 1))}
                    aria-label="Mois suivant"
                    className="p-1 border border-gray-200 rounded-sm text-gray-500 hover:bg-gray-50 transition-colors"
                >
                    <ChevronRight className="w-3.5 h-3.5" />
                </button>
                <span className="text-[11px] text-gray-500">
                    {sentCount === 0 && comingCount === 0
                        ? "aucun envoi ce mois-ci"
                        : [
                            sentCount > 0 ? `${sentCount} envoyé${sentCount > 1 ? "s" : ""}` : null,
                            comingCount > 0 ? `${comingCount} à venir` : null,
                        ].filter(Boolean).join(" · ")}
                </span>
                {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />}
                {cal?.remaining !== null && cal?.remaining !== undefined && (
                    <span className="text-[11px] text-gray-400">
                        · {cal.remaining} envoi{cal.remaining > 1 ? "s" : ""} restant{cal.remaining > 1 ? "s" : ""} dans la série
                    </span>
                )}
                <button
                    onClick={() => setMonth(startOfMonth(new Date()))}
                    className="ml-auto px-2 py-1 border border-gray-200 rounded-sm text-[11px] text-gray-600 hover:bg-gray-50 transition-colors"
                >
                    Ce mois-ci
                </button>
            </div>

            {cal?.paused && (
                <div className="mb-2 p-2 bg-amber-50 border border-amber-200 rounded-sm text-[11px] text-amber-800">
                    Profil en pause : les envois prévus ci-dessous ne partiront pas tant qu'il n'est pas réactivé.
                </div>
            )}
            {error && <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded-sm text-xs text-red-700">{error}</div>}

            <div className="grid grid-cols-7 border border-b-0 border-gray-200 rounded-t-sm bg-gray-50">
                {WEEKDAYS.map((d, i) => (
                    <div key={i} className="px-2 py-1 text-[10px] text-gray-400 text-center">{d}</div>
                ))}
            </div>
            <div className="grid grid-cols-7 border-l border-t border-gray-200 rounded-b-sm overflow-hidden">
                {days.map((day) => {
                    const key = dayKey(day);
                    const dayEntries = byDay.get(key) ?? [];
                    const outside = !sameMonth(day, month);
                    const future = day >= today;
                    const isDrop = dropDay === key;

                    return (
                        <div
                            key={key}
                            onDragOver={(e) => {
                                if (!dragKey || !future) return;
                                e.preventDefault();
                                e.dataTransfer.dropEffect = "move";
                                if (dropDay !== key) setDropDay(key);
                            }}
                            onDragLeave={() => { if (dropDay === key) setDropDay(null); }}
                            onDrop={(e) => { e.preventDefault(); void drop(day); }}
                            className={`group border-r border-b border-gray-200 p-1.5 min-h-[110px] transition-colors ${
                                isDrop ? "bg-sky-50 ring-2 ring-inset ring-sky-300" : outside ? "bg-gray-50/60" : "bg-white"
                            }`}
                        >
                            <div className="flex items-center justify-between gap-1">
                                <span
                                    className={`text-[10px] leading-none ${
                                        key === todayKey
                                            ? "bg-black text-white rounded-full w-4 h-4 flex items-center justify-center"
                                            : outside ? "text-gray-300" : "text-gray-400"
                                    }`}
                                >
                                    {day.getDate()}
                                </span>
                                {future && (
                                    <button
                                        type="button"
                                        onClick={() => setCreateDay(day)}
                                        title="Ajouter un envoi ponctuel ce jour-là"
                                        aria-label="Ajouter un envoi ponctuel ce jour-là"
                                        className="p-0.5 rounded-sm text-gray-300 opacity-0 group-hover:opacity-100 focus:opacity-100 hover:text-black hover:bg-gray-100 transition"
                                    >
                                        <Plus className="w-3 h-3" />
                                    </button>
                                )}
                            </div>

                            {dayEntries.map((entry) => (
                                <CalendarEntryView
                                    key={entry.key}
                                    entry={entry}
                                    html={entry.draftId ? htmlById[entry.draftId] : undefined}
                                    compact={dayEntries.length > 1}
                                    dimmed={Boolean(cal?.paused) && entry.status === "planned"}
                                    onOpen={() => open(entry)}
                                    onDetails={() => setDetailsKey(entry.key)}
                                    onDragStart={() => setDragKey(entry.key)}
                                    onDragEnd={() => { setDragKey(null); setDropDay(null); }}
                                />
                            ))}
                        </div>
                    );
                })}
            </div>

            <CalendarLegend />

            {viewIndex >= 0 && (
                <MailModal
                    entry={viewable[viewIndex]}
                    html={viewable[viewIndex].draftId ? htmlById[viewable[viewIndex].draftId!] : undefined}
                    index={viewIndex}
                    total={viewable.length}
                    onStep={step}
                    onClose={closeViewer}
                    onOpenDraft={onOpenDraft}
                    onDetails={() => { const k = viewKey; setViewKey(null); setDetailsKey(k); }}
                />
            )}

            {detailsEntry && (
                <EntryDetails
                    entry={detailsEntry}
                    profileId={profileId}
                    onClose={() => setDetailsKey(null)}
                    onChanged={refresh}
                    onOpenDraft={onOpenDraft}
                    onView={() => { const k = detailsKey; setDetailsKey(null); setViewKey(k); }}
                />
            )}

            {createDay && (
                <OneOffDialog
                    day={createDay}
                    defaultTime={firstSlot ? hhmm(firstSlot.hour, firstSlot.minute) : "09:00"}
                    profileId={profileId}
                    onClose={() => setCreateDay(null)}
                    onCreated={refresh}
                />
            )}
        </div>
    );
}

/**
 * What one mailing is doing, on one screen: its calendar — what left, what is prepared, and
 * every send still to come.
 *
 * Only the calendar, deliberately: nothing renders open by default. A click on a mail opens it
 * big, and the ⓘ of each send opens its reviews and its own settings (date, time, skip, prepare
 * now). The weekly pattern itself is edited from "Modifier le profil".
 */
function ProfileDetail({ profileId, onBack, onOpenDraft, onEdit, onOpenFeedback }: {
    profileId: string;
    onBack: () => void;
    onOpenDraft: (id: string) => void;
    onEdit: () => void;
    onOpenFeedback: () => void;
}) {
    const [profile, setProfile] = useState<WeeklyMailProfile | null>(null);
    const [history, setHistory] = useState<WeeklyMailHistory | null>(null);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        try {
            const [detail, hist] = await Promise.all([
                getProfile(profileId),
                getProfileHistory(profileId),
            ]);
            setProfile(detail.profile);
            setHistory(hist);
        } catch (err: any) {
            setError(err?.response?.data?.error ?? "Impossible de charger ce profil.");
        }
    }, [profileId]);

    useEffect(() => { void load(); }, [load]);

    // Only the header needs refreshing after a calendar action (its "prochain envoi").
    const reloadHeader = useCallback(() => {
        getProfile(profileId).then((d) => setProfile(d.profile)).catch(() => {});
    }, [profileId]);

    if (!profile) {
        return (
            <div>
                <button onClick={onBack} className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-black mb-4">
                    <ArrowLeft className="w-3.5 h-3.5" /> Tous les profils
                </button>
                {error
                    ? <div className="p-3 bg-red-50 border border-red-200 rounded-sm text-sm text-red-700">{error}</div>
                    : <div className="flex items-center gap-2 text-sm text-gray-400 p-6"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</div>}
            </div>
        );
    }

    return (
        <div>
            <button onClick={onBack} className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-black mb-4">
                <ArrowLeft className="w-3.5 h-3.5" /> Tous les profils
            </button>

            <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
                <div>
                    <h1 className="text-xl font-semibold text-gray-900">{profile.name}</h1>
                    <div className="flex items-center gap-4 mt-1 text-sm text-gray-500 flex-wrap">
                        <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {scheduleLabel(profile.schedule)}</span>
                        <span className="inline-flex items-center gap-1">
                            <Users className="w-3.5 h-3.5" /> {profile.recipients.map((r) => r.email).join(", ") || "aucun destinataire"}
                        </span>
                        {profile.nextSendAt && (
                            <span className="inline-flex items-center gap-1">
                                <Send className="w-3.5 h-3.5" /> Prochain envoi {fmtDateTime(profile.nextSendAt)}
                            </span>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <button onClick={onOpenFeedback} className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-sm text-xs text-gray-600 hover:bg-gray-50 transition-colors">
                        <MessageSquare className="w-3.5 h-3.5" /> Avis
                    </button>
                    <button onClick={onEdit} className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-sm text-xs text-gray-600 hover:bg-gray-50 transition-colors">
                        <PencilLine className="w-3.5 h-3.5" /> Modifier le profil
                    </button>
                </div>
            </div>

            {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-sm text-sm text-red-700">{error}</div>}

            <Section
                icon={Calendar}
                title={
                    history
                        ? `Calendrier des envois (${history.counts.sends} envoi${history.counts.sends > 1 ? "s" : ""} · ${history.counts.uniqueEstablishments} établissement${history.counts.uniqueEstablishments > 1 ? "s" : ""})`
                        : "Calendrier des envois"
                }
            >
                <SendCalendar profile={profile} onOpenDraft={onOpenDraft} onChanged={reloadHeader} />
            </Section>
        </div>
    );
}

// ─── Profile reviews ──────────────────────────────────────────────────────────

function ProfileFeedback({ profileId, onBack }: { profileId: string; onBack: () => void }) {
    const [data, setData] = useState<WeeklyMailProfileFeedback | null>(null);
    const [profileName, setProfileName] = useState("");
    const [filter, setFilter] = useState<"all" | "relevant" | "not_relevant" | "commented">("all");
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [feedback, { profile }] = await Promise.all([
                    getProfileFeedback(profileId),
                    getProfile(profileId),
                ]);
                if (cancelled) return;
                setData(feedback);
                setProfileName(profile.name);
            } catch (err: any) {
                if (!cancelled) setError(err?.response?.data?.error ?? "Impossible de charger les avis.");
            }
        })();
        return () => { cancelled = true; };
    }, [profileId]);

    const items = (data?.items ?? []).filter((f) =>
        filter === "all" ? true
            : filter === "commented" ? Boolean(f.comment)
            : f.vote === filter
    );

    return (
        <div>
            <button onClick={onBack} className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-black mb-4">
                <ArrowLeft className="w-3.5 h-3.5" /> Tous les profils
            </button>

            <div className="mb-5">
                <h1 className="text-xl font-semibold text-gray-900">Avis des destinataires</h1>
                <p className="text-sm text-gray-500 mt-1">
                    {profileName ? `Profil « ${profileName} » — ` : ""}
                    retours envoyés depuis les boutons du mail, sans connexion.
                </p>
            </div>

            {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-sm text-sm text-red-700">{error}</div>}

            {data === null ? (
                <div className="flex items-center gap-2 text-sm text-gray-400 p-6">
                    <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
                </div>
            ) : data.items.length === 0 ? (
                <div className="border border-dashed border-gray-300 rounded-sm p-10 text-center">
                    <MessageSquare className="w-6 h-6 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-500">Aucun avis pour l'instant.</p>
                    <p className="text-xs text-gray-400 mt-1">
                        Les retours apparaissent ici dès qu'un destinataire clique sur 👍 ou 👎 dans le mail.
                    </p>
                </div>
            ) : (
                <>
                    <div className="flex items-center gap-2 mb-4 flex-wrap">
                        {([
                            { key: "all" as const, label: `Tous (${data.counts.total})` },
                            { key: "relevant" as const, label: `Correspondent (${data.counts.relevant})` },
                            { key: "not_relevant" as const, label: `Ne correspondent pas (${data.counts.notRelevant})` },
                            { key: "commented" as const, label: `Avec commentaire (${data.counts.withComment})` },
                        ]).map(({ key, label }) => (
                            <button
                                key={key} onClick={() => setFilter(key)}
                                className={`px-3 py-1 rounded-sm text-xs border transition-colors ${
                                    filter === key
                                        ? "bg-black text-white border-black"
                                        : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
                                }`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>

                    {/*
                        Cards rather than a table. A review is only useful next to what was
                        sent — the SIRET to pull the record up, the signals it carried, the
                        angle it was pitched under — and none of that fits in a row without
                        turning into a horizontal scroll.
                    */}
                    <div className="grid gap-2">
                        {items.map((f) => (
                            <div key={f._id} className="border border-gray-200 rounded-sm bg-white p-3">
                                <div className="flex items-start gap-2 flex-wrap">
                                    <span className={`px-1.5 py-0.5 text-[10px] rounded whitespace-nowrap ${
                                        f.vote === "relevant" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"
                                    }`}>
                                        {f.vote === "relevant" ? "👍 Correspond" : "👎 Ne correspond pas"}
                                    </span>
                                    <span className="text-sm font-medium text-gray-900">{f.establishmentName}</span>
                                    {f.slotLabel && (
                                        <span className={`px-1.5 py-0.5 text-[10px] rounded ${SLOT_STYLES[f.slot ?? "signal"] ?? SLOT_STYLES.signal}`}>
                                            {f.slotLabel}
                                        </span>
                                    )}
                                    <span className="ml-auto text-[11px] text-gray-400 whitespace-nowrap">
                                        {f.sentAt ? `envoyé le ${fmtDate(f.sentAt)}` : f.scheduledFor ? `prévu ${fmtDate(f.scheduledFor)}` : "—"}
                                        {" · "}répondu le {fmtDate(f.respondedAt)}
                                    </span>
                                </div>

                                {/* The identifier first: it is what the CRM and the client both work from. */}
                                <div className="flex items-center gap-3 mt-1.5 text-[11px] text-gray-500 flex-wrap">
                                    {f.siret ? (
                                        <a
                                            href={`https://www.pappers.fr/recherche?q=${f.siret}`}
                                            target="_blank" rel="noopener noreferrer"
                                            title="Ouvrir la fiche entreprise"
                                            className="font-mono text-gray-700 hover:underline inline-flex items-center gap-1"
                                        >
                                            <Hash className="w-3 h-3" />{f.siret}
                                        </a>
                                    ) : f.siren ? (
                                        <span className="font-mono text-gray-500">SIREN {f.siren}</span>
                                    ) : (
                                        <span className="text-gray-300">SIRET inconnu</span>
                                    )}
                                    {(f.postcode || f.city) && (
                                        <span className="inline-flex items-center gap-1">
                                            <MapPin className="w-3 h-3" />{[f.postcode, f.city].filter(Boolean).join(" ")}
                                        </span>
                                    )}
                                    {f.owner && <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" />{f.owner}</span>}
                                    {f.rating && <span className="inline-flex items-center gap-1"><Star className="w-3 h-3" />{f.rating}</span>}
                                    {f.phone && <span>{f.phone}</span>}
                                    {f.email && <span className="truncate max-w-[220px]">{f.email}</span>}
                                </div>

                                {/* Why it was in the brief — the half that makes the vote actionable. */}
                                {f.reason && (
                                    <p className="text-[11px] text-gray-600 mt-2">
                                        <span className="text-gray-400">Argument envoyé : </span>
                                        {f.reasonTag && <span className="text-emerald-700 font-medium">{f.reasonTag} · </span>}
                                        {f.reason}
                                    </p>
                                )}

                                {f.signals.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mt-2">
                                        {f.signals.slice(0, 5).map((sig, i) => (
                                            <span key={i} className="px-2 py-0.5 bg-emerald-50 text-emerald-700 text-[10px] rounded-full border border-emerald-100">
                                                {sig.label}{sig.date ? ` · ${fmtDate(sig.date)}` : ""}
                                            </span>
                                        ))}
                                    </div>
                                )}

                                {[...f.categories, ...f.subCategories].length > 0 && (
                                    <div className="flex flex-wrap gap-1 mt-1.5">
                                        {[...f.categories, ...f.subCategories].slice(0, 8).map((tag) => (
                                            <span key={tag} className="px-1.5 py-0.5 bg-gray-100 text-gray-500 text-[10px] rounded">{tag}</span>
                                        ))}
                                    </div>
                                )}

                                {f.comment && (
                                    <p className="text-xs text-gray-700 mt-2 border-l-2 border-gray-200 pl-2">« {f.comment} »</p>
                                )}

                                <p className="text-[11px] text-gray-400 mt-2">{f.recipientEmail ?? "destinataire inconnu"}</p>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

type View =
    | { kind: "list" }
    | { kind: "detail"; id: string }
    | { kind: "profile"; id: string | null }
    | { kind: "draft"; id: string }
    | { kind: "feedback"; id: string };

export default function WeeklyMail() {
    const [view, setView] = useState<View>({ kind: "list" });

    return (
        <div className="p-6 max-w-5xl mx-auto">
            <BONavigation />
            {view.kind === "list" && (
                <ProfileList
                    onOpen={(id) => setView({ kind: "profile", id })}
                    onOpenDetail={(id) => setView({ kind: "detail", id })}
                    onOpenDraft={(id) => setView({ kind: "draft", id })}
                    onOpenFeedback={(id) => setView({ kind: "feedback", id })}
                />
            )}
            {view.kind === "detail" && (
                <ProfileDetail
                    profileId={view.id}
                    onBack={() => setView({ kind: "list" })}
                    onOpenDraft={(id) => setView({ kind: "draft", id })}
                    onEdit={() => setView({ kind: "profile", id: view.id })}
                    onOpenFeedback={() => setView({ kind: "feedback", id: view.id })}
                />
            )}
            {view.kind === "profile" && (
                <ProfileEditor
                    profileId={view.id}
                    onBack={() => setView({ kind: "list" })}
                    onOpenDraft={(id) => setView({ kind: "draft", id })}
                    // Keeps the view in sync with the profile that was just created,
                    // so going back and forth never spawns a second one
                    onCreated={(id) => setView({ kind: "profile", id })}
                />
            )}
            {view.kind === "draft" && (
                <DraftEditor draftId={view.id} onBack={() => setView({ kind: "list" })} />
            )}
            {view.kind === "feedback" && (
                <ProfileFeedback profileId={view.id} onBack={() => setView({ kind: "list" })} />
            )}
        </div>
    );
}
